import { useState, useRef, useEffect, useCallback, useMemo, memo, createContext, useContext, type HTMLAttributes, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { Bot, Send, Square, Check, X, Loader2, Wifi, WifiOff, ChevronRight, ChevronDown, MessageCircleQuestion, Slash, Zap, Terminal, FileText, LogIn, LogOut, ExternalLink, Key, Shield, MessageSquarePlus, Copy, History, Archive, Mic, AudioLines, Volume2 } from 'lucide-react'
import Markdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeHighlight from 'rehype-highlight'
import Panel from './Panel'
import { useClaudeSession, type ChatMessage, type ContentBlock, type PermissionRequest, type QuestionRequest, type QuestionItem, type ToolProgress, type ImageAttachment, type LoginState } from '../../hooks/useClaudeSession'
import { formatRelativeTime } from '../../lib/format'
import { claudeBridgeSessionId } from '../../lib/claudeSessionId'
import { extractSpeakableText, extractCopyText } from '../../lib/speech'
import { ListenButton } from '../ListenButton'
import { AudioTransportBar } from './AudioTransportBar'
import { useSpeech, blessAutoplayElement, startVoiceKeepAlive, stopVoiceKeepAlive, playEarcon, startProgressLoop, stopProgressLoop } from '../../lib/speechPlayer'
import { useVoiceRecognition } from '../../hooks/useVoiceRecognition'
import { useVoiceSendBuffer } from '../../hooks/useVoiceSendBuffer'
import { useVoiceCommentary } from '../../hooks/useVoiceCommentary'
import { useOnlineStatus } from '../../hooks/useOnlineStatus'

// ─── Image Helpers ───────────────────────────────────────────────────────────

// Image formats supported by Claude API as image content blocks
const SUPPORTED_IMAGE_TYPES = new Set(['image/jpeg', 'image/png', 'image/gif', 'image/webp'])

// Anthropic rejects images > this on either axis for many-image requests.
// Dashboard-side downscale keeps the user's session from getting poisoned
// by a single large screenshot.
const MAX_IMAGE_DIM = 2000

function decodeImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const img = new Image()
    img.onload = () => { URL.revokeObjectURL(url); resolve(img) }
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Failed to load image')) }
    img.src = url
  })
}

function drawScaled(img: HTMLImageElement, maxDim: number): HTMLCanvasElement {
  const w = img.naturalWidth || img.width
  const h = img.naturalHeight || img.height
  const scale = Math.min(1, maxDim / Math.max(w, h))
  const canvas = document.createElement('canvas')
  canvas.width = Math.max(1, Math.round(w * scale))
  canvas.height = Math.max(1, Math.round(h * scale))
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('Canvas not supported')
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
  return canvas
}

async function readFileAsBase64(file: File): Promise<{ base64: string; mediaType: string }> {
  // SVG: text payload, no raster dimension limit applies. Pass through as-is;
  // the bridge converts it to a text block downstream.
  if (file.type === 'image/svg+xml') {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => {
        const base64 = (reader.result as string).split(',')[1]
        resolve({ base64, mediaType: file.type })
      }
      reader.onerror = () => reject(new Error('Failed to read SVG'))
      reader.readAsDataURL(file)
    })
  }

  const img = await decodeImage(file)
  const w = img.naturalWidth || img.width
  const h = img.naturalHeight || img.height
  const needsDownscale = Math.max(w, h) > MAX_IMAGE_DIM
  const needsTranscode = !SUPPORTED_IMAGE_TYPES.has(file.type)

  // Fast path: already within limits AND in a supported format → keep original bytes.
  // Avoids re-encoding (lossy for JPEG, wastes CPU for PNG).
  if (!needsDownscale && !needsTranscode) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => {
        const base64 = (reader.result as string).split(',')[1]
        resolve({ base64, mediaType: file.type })
      }
      reader.onerror = () => reject(new Error('Failed to read image'))
      reader.readAsDataURL(file)
    })
  }

  // Draw to canvas, exporting as JPEG (smaller) for non-transparency-sensitive
  // formats, otherwise PNG. Target format must be in SUPPORTED_IMAGE_TYPES.
  const canvas = drawScaled(img, MAX_IMAGE_DIM)
  const targetMime = file.type === 'image/jpeg' ? 'image/jpeg' : 'image/png'
  const quality = targetMime === 'image/jpeg' ? 0.92 : undefined
  const dataUrl = canvas.toDataURL(targetMime, quality)
  const base64 = dataUrl.split(',')[1]
  if (needsDownscale) {
    console.log(`[claude-panel] Downscaled ${w}x${h} → ${canvas.width}x${canvas.height} (${targetMime})`)
  }
  return { base64, mediaType: targetMime }
}

// ─── Typewriter Hook ─────────────────────────────────────────────────────────

/**
 * Smooths chunky streaming text by revealing characters gradually.
 * Buffers incoming text and reveals it at a steady rate using requestAnimationFrame.
 */
function useTypewriter(source: string, charsPerFrame = 3): string {
  const [displayed, setDisplayed] = useState('')
  const targetRef = useRef('')
  const displayedRef = useRef('')
  const rafRef = useRef<number>(0)

  // When source resets (new message), reset displayed immediately
  useEffect(() => {
    if (source === '') {
      setDisplayed('')
      displayedRef.current = ''
      targetRef.current = ''
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
      return
    }
    targetRef.current = source
  }, [source])

  useEffect(() => {
    const tick = () => {
      const target = targetRef.current
      const current = displayedRef.current
      if (current.length < target.length) {
        const next = target.slice(0, current.length + charsPerFrame)
        displayedRef.current = next
        setDisplayed(next)
      }
      rafRef.current = requestAnimationFrame(tick)
    }
    rafRef.current = requestAnimationFrame(tick)
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
    }
  }, [charsPerFrame])

  return displayed
}

interface ClaudePanelProps {
  appName: string
  /** Active work area; 'main'/undefined = primary. Folds into the bridge session id. */
  session?: string
  onClose?: () => void
  closeable?: boolean
  isMaximized?: boolean
  onMaximize?: () => void
  /** When set, shows a header button that hands the main pane over to the terminal. */
  onSwitchToTerminal?: () => void
}

// Generate a stable session ID per app (persists across re-renders, not across page loads)
// Permission modes — cycled with Shift+Tab
const PERMISSION_MODES = ['bypassPermissions', 'acceptEdits', 'default', 'plan'] as const
const MODE_LABELS: Record<string, string> = { bypassPermissions: 'Full Auto', acceptEdits: 'Accept Edits', default: 'Ask First', plan: 'Plan' }

// Code block with a ChatGPT-style Copy button pinned top-right. Reads from
// the rendered <pre>'s innerText so we capture the post-rehype-highlight
// payload exactly as the user sees it. Button stays visible (no hover-only)
// so mobile users get an affordance too.
function CodeBlockWithCopy({ children, className, ...preProps }: { children?: ReactNode } & HTMLAttributes<HTMLPreElement>) {
  const [copied, setCopied] = useState(false)
  const preRef = useRef<HTMLPreElement>(null)

  const handleCopy = useCallback(async () => {
    const text = preRef.current?.innerText ?? ''
    if (!text) return
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text)
      } else {
        const ta = document.createElement('textarea')
        ta.value = text
        ta.style.position = 'fixed'
        ta.style.opacity = '0'
        document.body.appendChild(ta)
        ta.select()
        document.execCommand('copy')
        document.body.removeChild(ta)
      }
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch (err) {
      console.error('[claude-panel] copy failed:', err)
    }
  }, [])

  return (
    <div className="relative group">
      <pre ref={preRef} className={className} {...preProps}>{children}</pre>
      <button
        type="button"
        onClick={handleCopy}
        aria-label={copied ? 'Copied' : 'Copy code'}
        className="absolute top-1.5 right-1.5 inline-flex items-center justify-center h-6 w-6 rounded text-muted-foreground/70 hover:text-foreground hover:bg-background/60 transition-colors"
      >
        {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
      </button>
    </div>
  )
}

// Shared component overrides for all <Markdown> renders. Wraps tables in
// a horizontally-scrollable container so wide tables (common in Claude's
// replies) don't force the whole panel to scroll sideways — they scroll
// inside their own bounding box like a real data grid. Wraps <pre> with
// a Copy button (see CodeBlockWithCopy) for the ChatGPT-style affordance.
const MARKDOWN_COMPONENTS = {
  table: ({ node: _node, ...props }: { node?: unknown } & HTMLAttributes<HTMLTableElement>) => (
    <div className="overflow-x-auto -mx-1 my-2 rounded border border-border/40">
      <table {...props} className="min-w-full" />
    </div>
  ),
  pre: ({ node: _node, ...props }: { node?: unknown } & HTMLAttributes<HTMLPreElement>) => (
    <CodeBlockWithCopy {...props} />
  ),
}

// Memoized Markdown wrapper. Without memo, every parent re-render (every keystroke
// in the input textarea) re-parses every message's markdown (remark-gfm +
// rehype-highlight). That cost scales linearly with chat length — long sessions
// produced ~115ms+ keystroke lag. With memo, the parse only re-runs when `text`
// actually changes.
const MarkdownView = memo(function MarkdownView({ text }: { text: string }) {
  return (
    <Markdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeHighlight]} components={MARKDOWN_COMPONENTS}>
      {text}
    </Markdown>
  )
})
export default function ClaudePanel({
  appName,
  session,
  onClose,
  closeable = true,
  isMaximized,
  onMaximize,
  onSwitchToTerminal,
}: ClaudePanelProps) {
  // Bridge session id derived from the active work area via the shared helper.
  // main/empty/undefined → `workspace-${appName}` (byte-identical to before);
  // worktree → `workspace-${appName}-${session}`. Changing it reconnects the WS.
  const sessionId = useMemo(() => claudeBridgeSessionId(appName, session), [appName, session])
  // Which agent drives this panel session. Persisted per work area so a
  // reload comes back to the agent the user last chose; the bridge is the
  // source of truth and falls back to claude for anything it doesn't know.
  const [agentKind, setAgentKind] = useState<string>(() => {
    try {
      return localStorage.getItem(`cc-panel-agent:${sessionId}`) || 'claude'
    } catch {
      return 'claude'
    }
  })
  useEffect(() => {
    try { localStorage.setItem(`cc-panel-agent:${sessionId}`, agentKind) } catch { /* private mode */ }
  }, [sessionId, agentKind])

  const { offline } = useOnlineStatus()
  const {
    connected,
    cliConnected,
    messages,
    sessionState,
    pendingPermissions,
    pendingQuestions,
    streamingText,
    streamingThinking,
    isStreaming,
    isCompacting,
    activeTools,
    sendMessage,
    respondToPermission,
    respondToQuestion,
    interrupt,
    setModel,
    setPermissionMode,
    loginState,
    startLogin,
    submitLoginCode,
    logout,
    newSession,
    phase,
    conversations,
    listConversations,
    resumeConversation,
    setAgent,
  } = useClaudeSession(appName, sessionId, agentKind)

  const [input, setInput] = useState('')
  const [slashMenuIndex, setSlashMenuIndex] = useState(0)
  const [images, setImages] = useState<ImageAttachment[]>([])
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const messageFeedRef = useRef<HTMLDivElement>(null)
  // Auto-scroll only when the user is already at the bottom of the message
  // feed. Tracked in a ref (not state) so streaming chunks don't churn renders
  // and the scroll handler stays stable. Defaults to true so the first batch
  // of messages auto-scrolls without waiting for user interaction.
  const isAtBottomRef = useRef(true)
  // Tracks whether the first auto-scroll for this mount has already fired.
  // Distinguishes the "page just reloaded and `useClaudeSession` hydrated
  // `messages` synchronously from sessionStorage at hook construction" case
  // (which needs an INSTANT snap after the markdown actually lays out — see
  // useEffect below for the double-rAF rationale) from the subsequent
  // "new message arrived during this session" case (which keeps the existing
  // smooth-scroll behavior). Reset to false on sessionId change so workspace-
  // switching gets the same instant snap as a page reload.
  const initialScrollDoneRef = useRef(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const slashMenuRef = useRef<HTMLDivElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const prevInputRef = useRef('')

  // Input history (readline-style up/down arrow)
  const inputHistoryRef = useRef<string[]>([])
  const historyIndexRef = useRef(-1)
  const savedInputRef = useRef('')

  // Stall detection — warn when no response arrives after sending
  const [stalled, setStalled] = useState(false)
  const stallTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Build slash command list from session state (depend on arrays, not whole object).
  // The CLI's init catalog lists invocable skills under slash_commands too — dedupe
  // by name so each entry appears once, and sort for a scannable menu.
  const slashCommands = sessionState?.slash_commands
  const slashSkills = sessionState?.skills
  const commandMeta = sessionState?.command_meta
  const allCommands = useMemo(() => {
    const seen = new Set<string>()
    const cmds: { name: string; type: 'command' | 'skill'; description?: string }[] = []
    if (slashCommands) {
      for (const c of slashCommands) {
        if (seen.has(c)) continue
        seen.add(c)
        cmds.push({ name: c, type: 'command', description: commandMeta?.[c]?.description })
      }
    }
    if (slashSkills) {
      for (const s of slashSkills) {
        if (seen.has(s)) continue
        seen.add(s)
        cmds.push({ name: s, type: 'skill', description: commandMeta?.[s]?.description })
      }
    }
    cmds.sort((a, b) => a.name.localeCompare(b.name))
    return cmds
  }, [slashCommands, slashSkills, commandMeta])

  // Usage hint once a command is picked/typed (input = "/name …"), mirroring
  // the CLI's argument-hint affordance. Sourced from the same frontmatter.
  const activeCommandHint = useMemo(() => {
    const m = input.match(/^\/(\S+)\s/)
    if (!m) return null
    const meta = commandMeta?.[m[1]]
    if (!meta || (!meta.argument_hint && !meta.description)) return null
    return { name: m[1], argument_hint: meta.argument_hint, description: meta.description }
  }, [input, commandMeta])

  // Filter commands based on current input.
  // Use a ref to cache the last result and avoid creating new array references
  // on every render (which would destabilize handleKeyDown's useCallback).
  const filteredCommandsRef = useRef<{ name: string; type: 'command' | 'skill'; description?: string }[]>([])
  const lastFilterInputRef = useRef('')
  if (input !== lastFilterInputRef.current) {
    lastFilterInputRef.current = input
    const m = input.match(/^\/(\S*)$/)
    if (!m) {
      filteredCommandsRef.current = []
    } else {
      const q = m[1].toLowerCase()
      filteredCommandsRef.current = q ? allCommands.filter(c => c.name.toLowerCase().startsWith(q)) : allCommands
    }
  }
  const filteredCommands = filteredCommandsRef.current
  const slashMenuVisible = filteredCommands.length > 0

  // Reset menu index when input changes
  if (input !== prevInputRef.current) {
    prevInputRef.current = input
    setSlashMenuIndex(0)
  }

  // Smooth streaming text via typewriter effect
  const smoothText = useTypewriter(streamingText)

  // Scroll active menu item into view
  useEffect(() => {
    if (!slashMenuVisible || !slashMenuRef.current) return
    const item = slashMenuRef.current.querySelector(`[data-cmd-index="${slashMenuIndex}"]`)
    item?.scrollIntoView({ block: 'nearest' })
  }, [slashMenuIndex, slashMenuVisible])

  // Clear stall indicator when any response arrives
  useEffect(() => {
    if (isStreaming || streamingText || streamingThinking || activeTools.size > 0) {
      setStalled(false)
      if (stallTimerRef.current) { clearTimeout(stallTimerRef.current); stallTimerRef.current = null }
    }
  }, [isStreaming, streamingText, streamingThinking, activeTools])

  // Start stall timer when last message is a user_message (manual or auto-build),
  // clear when a response arrives
  useEffect(() => {
    if (messages.length > 0) {
      const last = messages[messages.length - 1]
      if (
        last.type === 'assistant' || last.type === 'result'
        // A backfilled local slash command (/copy, /cost, …) resolved
        // entirely inside the CLI — finished business, not an open turn,
        // no matter how recent its timestamp. See transcript.js.
        || (last.type === 'user_message' && last.awaitingReply === false)
      ) {
        setStalled(false)
        if (stallTimerRef.current) { clearTimeout(stallTimerRef.current); stallTimerRef.current = null }
      } else if (
        last.type === 'user_message'
        && !stallTimerRef.current
        // Recent sends only — a backfilled transcript ending on an old user
        // message must not trigger "response taking longer than usual".
        && Date.now() - (last.timestamp || 0) < 60_000
      ) {
        // User message with no response yet — start stall timer
        setStalled(false)
        stallTimerRef.current = setTimeout(() => setStalled(true), 15000)
      }
    }
  }, [messages])

  // Auto-scroll on new messages and streaming updates ONLY when the user is
  // already at the bottom of the feed. Without this `isAtBottomRef` gate,
  // scrolling up to re-read an earlier message during a live stream is
  // impossible — every chunk arrival yanks the view back to the latest
  // content. (Anti-yank gate added in PR #163 — DO NOT REMOVE.)
  //
  // First-effect-pass handling (the "halfway-scroll on reload" fix):
  // the Claude panel renders content in stages — react-markdown commits,
  // rehype-highlight syntax-highlights each fenced block (one per work
  // unit), SubagentThread mounts recursively, web fonts swap in and shift
  // line-heights. A one-shot snap (even at two animation frames) lands at
  // "the bottom as of ~32 ms after mount," and everything that renders
  // after that pushes the real bottom further down — leaving the user
  // halfway through the conversation.
  //
  // Snap-loop: keep forcing scrollTop = scrollHeight on every animation
  // frame for an 800 ms window, so late-rendering chunks can never push
  // the bottom past us. Bail early on user input (wheel / touchstart /
  // keydown) so we never override an intentional scroll.
  useEffect(() => {
    if (messages.length === 0) return
    if (!isAtBottomRef.current) return

    if (!initialScrollDoneRef.current) {
      initialScrollDoneRef.current = true
      const el = messageFeedRef.current
      if (!el) {
        messagesEndRef.current?.scrollIntoView({ behavior: 'auto', block: 'end' })
        return
      }

      const startedAt = performance.now()
      const WINDOW_MS = 800
      let userInterrupted = false

      const onUserGesture = () => { userInterrupted = true }
      el.addEventListener('wheel', onUserGesture, { passive: true, once: true })
      el.addEventListener('touchstart', onUserGesture, { passive: true, once: true })
      el.addEventListener('keydown', onUserGesture, { once: true })

      const cleanup = () => {
        el.removeEventListener('wheel', onUserGesture)
        el.removeEventListener('touchstart', onUserGesture)
        el.removeEventListener('keydown', onUserGesture)
      }

      const tick = (now: number) => {
        if (userInterrupted) { cleanup(); return }
        if (now - startedAt > WINDOW_MS) { cleanup(); return }
        // Force-pin to the bottom. Each iteration absorbs whatever
        // scrollHeight growth happened since the last frame.
        el.scrollTop = el.scrollHeight
        requestAnimationFrame(tick)
      }
      requestAnimationFrame(tick)
      return
    }

    // Scroll the FEED CONTAINER only — never scrollIntoView, which walks up
    // and can scroll the page/body too. With the mobile keyboard open, iOS
    // gives the body scroll room; every new message (each tool call!) then
    // yanked the whole layout up, leaving a keyboard-sized void under the
    // composer.
    const feedEl = messageFeedRef.current
    if (feedEl) feedEl.scrollTo({ top: feedEl.scrollHeight, behavior: 'smooth' })
  }, [messages, pendingPermissions, pendingQuestions, smoothText])

  // Track whether the user is at the bottom of the message feed. ~64px
  // threshold gives some slack for half-visible final lines and momentum
  // scroll overshoot.
  const handleMessageFeedScroll = useCallback(() => {
    const el = messageFeedRef.current
    if (!el) return
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight
    isAtBottomRef.current = distance < 64
  }, [])

  // Bottom-anchor the feed across container RESIZES. When the mobile keyboard
  // opens, the feed shrinks but the browser preserves scrollTop — the view
  // suddenly shows an OLDER slice of the conversation while the newest
  // messages hide under the fold. If the user was at the bottom, keep them at
  // the bottom through any size change (keyboard open/close, panel resize);
  // if they had scrolled up to read, leave their position alone.
  useEffect(() => {
    const el = messageFeedRef.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(() => {
      if (isAtBottomRef.current) {
        el.scrollTop = el.scrollHeight
      }
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  // When the user switches to a different workspace's chat (sessionId
  // changes), the previous session's "scrolled up" state must not leak
  // into the new feed. Reset back to "at bottom" so the first auto-scroll
  // in the new session lands cleanly. Also reset `initialScrollDoneRef`
  // so the new session gets the same instant double-rAF snap a page reload
  // gets — without this, switching sessions would smooth-scroll from the
  // top of the new feed, which has the same halfway-landing race the
  // first-mount fix above is meant to solve.
  useEffect(() => {
    isAtBottomRef.current = true
    initialScrollDoneRef.current = false
  }, [sessionId])

  const selectCommand = useCallback((cmd: { name: string }) => {
    setInput(`/${cmd.name} `)
    textareaRef.current?.focus()
  }, [])

  const handleSubmit = useCallback((e?: React.FormEvent) => {
    e?.preventDefault()
    const text = input.trim()
    if (!text) return
    // Save to input history (avoid duplicating the last entry)
    const hist = inputHistoryRef.current
    if (hist.length === 0 || hist[hist.length - 1] !== text) {
      hist.push(text)
      if (hist.length > 100) hist.shift()
    }
    historyIndexRef.current = -1
    savedInputRef.current = ''
    // Submitting a new prompt = user is engaged with the latest turn. Force
    // auto-scroll back on even if they had scrolled up to read earlier
    // content; the new user message and the upcoming reply should be visible.
    isAtBottomRef.current = true
    sendMessage(text, images.length > 0 ? images : undefined)
    setInput('')
    setImages([])
    // Start stall timer — if no response in 15s, warn user
    setStalled(false)
    if (stallTimerRef.current) clearTimeout(stallTimerRef.current)
    stallTimerRef.current = setTimeout(() => setStalled(true), 15000)
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
    }
  }, [input, images, sendMessage])

  const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files) return
    const newImages: ImageAttachment[] = []
    for (const file of Array.from(files)) {
      if (!file.type.startsWith('image/')) continue
      const { base64, mediaType } = await readFileAsBase64(file)
      newImages.push({ media_type: mediaType, data: base64 })
    }
    setImages(prev => [...prev, ...newImages])
    e.target.value = ''
  }, [])

  const handlePaste = useCallback(async (e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items
    if (!items) return
    const newImages: ImageAttachment[] = []
    for (const item of Array.from(items)) {
      if (!item.type.startsWith('image/')) continue
      const file = item.getAsFile()
      if (!file) continue
      const { base64, mediaType } = await readFileAsBase64(file)
      newImages.push({ media_type: mediaType, data: base64 })
    }
    if (newImages.length > 0) {
      e.preventDefault()
      setImages(prev => [...prev, ...newImages])
    }
  }, [])

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (slashMenuVisible) {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setSlashMenuIndex(prev => (prev + 1) % filteredCommands.length)
        return
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        setSlashMenuIndex(prev => (prev - 1 + filteredCommands.length) % filteredCommands.length)
        return
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault()
        selectCommand(filteredCommands[slashMenuIndex])
        return
      }
      if (e.key === 'Escape') {
        e.preventDefault()
        setInput('')
        return
      }
    }
    // Input history navigation (readline-style) — only when not in slash menu
    const el = e.currentTarget
    if (e.key === 'ArrowUp' && el.selectionStart === 0 && el.selectionEnd === 0) {
      const hist = inputHistoryRef.current
      if (hist.length === 0) return
      e.preventDefault()
      if (historyIndexRef.current === -1) {
        // Entering history — save current input
        savedInputRef.current = input
        historyIndexRef.current = hist.length - 1
      } else if (historyIndexRef.current > 0) {
        historyIndexRef.current--
      }
      setInput(hist[historyIndexRef.current])
      return
    }
    if (e.key === 'ArrowDown' && historyIndexRef.current >= 0 && el.selectionStart === el.value.length) {
      e.preventDefault()
      const hist = inputHistoryRef.current
      if (historyIndexRef.current < hist.length - 1) {
        historyIndexRef.current++
        setInput(hist[historyIndexRef.current])
      } else {
        // Back to saved input
        historyIndexRef.current = -1
        setInput(savedInputRef.current)
      }
      return
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit()
    }
  }, [handleSubmit, input, slashMenuVisible, filteredCommands, slashMenuIndex, selectCommand])

  // Auto-resize textarea
  const handleTextareaInput = useCallback(() => {
    const el = textareaRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = Math.min(el.scrollHeight, 150) + 'px'
  }, [])

  // Determine if Claude is actively working
  const isWorking = useMemo(() => {
    // Disconnected/reconnecting = nothing is running that we could stop, and
    // the interrupt wouldn't reach the CLI anyway. Showing the Stop button +
    // typing dots then is a lie; they come back with the connection if a turn
    // is genuinely in flight.
    if (!connected || !cliConnected) return false
    if (isStreaming || isCompacting || activeTools.size > 0) return true
    if (pendingPermissions.length > 0) return true
    if (messages.length === 0) return false
    const last = messages[messages.length - 1]
    if (last.type === 'user_message') {
      // A backfilled local slash command (/copy, /cost, …) resolved entirely
      // inside the CLI — a `<local-command-stdout>` echo followed it in the
      // transcript, no assistant turn. That's finished business regardless
      // of how recent it looks (unlike recency, this can't be fooled by
      // switching from the terminal to the panel moments after running it).
      if (last.awaitingReply === false) return false
      // Otherwise, only a RECENT trailing user message means "reply on the
      // way". History backfilled from the CLI transcript can legitimately
      // end on an old user message (unanswered when the terminal session
      // closed) — that is finished business, not an active turn.
      return Date.now() - (last.timestamp || 0) < 2 * 60_000
    }
    if (last.type === 'assistant') {
      // A live turn ends with a `result` frame, so a trailing assistant
      // message normally means "still working". But history backfilled from
      // the CLI transcript (terminal turns, resumed conversations) has no
      // result frames and ALWAYS ends on an assistant message — trust the
      // bridge's ground-truth phase to tell the two apart. Unknown phase
      // (no frame yet) keeps the old assume-working behavior.
      return phase === null || phase === 'streaming' || phase === 'awaiting_permission' || phase === 'compacting'
    }
    return false
  }, [messages, pendingPermissions, isStreaming, isCompacting, activeTools, phase, connected, cliConnected])

  const currentMode = sessionState?.permissionMode || 'bypassPermissions'

  // Group messages into top-level and subagent threads
  const { topLevel: topLevelMessages, childrenByParent, agentMeta } = useMemo(() => {
    const meta = new Map<string, { description: string; agentType: string }>()
    const children = new Map<string, ChatMessage[]>()
    const top: ChatMessage[] = []

    // First pass: extract Agent/Task tool metadata
    for (const msg of messages) {
      if (msg.type !== 'assistant') continue
      for (const block of msg.message?.content || []) {
        if (block.type === 'tool_use' && (block.name === 'Agent' || block.name === 'Task') && block.id) {
          meta.set(block.id, {
            description: (block.input?.description as string) || 'Subagent',
            agentType: (block.input?.subagent_type as string) || '',
          })
        }
      }
    }

    // Collect all parent_tool_use_ids so we can detect orphaned children
    // (whose parent Agent block was lost in message merging)
    const allParentIds = new Set<string>()
    for (const msg of messages) {
      const parentId = (msg as { parent_tool_use_id?: string | null }).parent_tool_use_id
      if (parentId) allParentIds.add(parentId)
    }

    // Register unknown parents (orphaned) so children still get grouped
    for (const parentId of allParentIds) {
      if (!meta.has(parentId)) {
        meta.set(parentId, { description: 'Subagent', agentType: '' })
      }
    }

    // Second pass: partition by parent_tool_use_id
    for (const msg of messages) {
      const parentId = (msg as { parent_tool_use_id?: string | null }).parent_tool_use_id
      if (parentId && meta.has(parentId)) {
        let arr = children.get(parentId)
        if (!arr) { arr = []; children.set(parentId, arr) }
        arr.push(msg)
      } else {
        top.push(msg)
      }
    }

    return { topLevel: top, childrenByParent: children, agentMeta: meta }
  }, [messages])

  // Pure computation feeding the message-feed render below, hoisted out of
  // the JSX so it doesn't re-run on every render (e.g. composer keystrokes) —
  // extractSpeakableText alone is ~20 regex passes per turn-final message.
  type Orphan = { parentId: string; kids: ChatMessage[]; timestamp: number }
  const feedComputed = useMemo(() => {
    // Pre-compute the set of parent_tool_use_ids that DO have a
    // top-level parent Agent block we can render inline. Everything
    // else (where the parent was lost in message merging) becomes
    // an orphan we slot in by timestamp.
    const inlineParentIds = new Set<string>()
    for (const m of topLevelMessages) {
      if (m.type !== 'assistant') continue
      for (const block of m.message?.content || []) {
        if (block.type === 'tool_use' && block.id && (block.name === 'Agent' || block.name === 'Task')) {
          inlineParentIds.add(block.id)
        }
      }
    }
    const orphans: Orphan[] = []
    for (const [parentId, kids] of childrenByParent.entries()) {
      if (inlineParentIds.has(parentId)) continue
      const firstKidTs = (kids[0] as { timestamp?: number }).timestamp ?? 0
      orphans.push({ parentId, kids, timestamp: firstKidTs })
    }
    orphans.sort((a, b) => a.timestamp - b.timestamp)

    // Indices of assistant messages that END a turn — the final
    // summary/verdict Claude gives before it stops or you reply. An
    // assistant message qualifies when, looking forward, the next
    // assistant-or-user message is a USER message (or the thread ends) —
    // i.e. no further assistant reply follows in the same turn. Trailing
    // `result`/tool messages ("Done — 27.5s") are skipped. These keep
    // Copy/Listen permanently (one per turn), so replying to a reply does
    // not strip the actions off the earlier summary; intermediate
    // mid-turn assistant messages never get them.
    const turnFinalAssistant = new Set<number>()
    for (let k = 0; k < topLevelMessages.length; k++) {
      if (topLevelMessages[k].type !== 'assistant') continue
      let endsTurn = true
      for (let n = k + 1; n < topLevelMessages.length; n++) {
        const t = topLevelMessages[n].type
        if (t === 'assistant') { endsTurn = false; break }
        if (t === 'user_message') break
      }
      if (endsTurn) turnFinalAssistant.add(k)
    }

    // Copy/Listen must cover the WHOLE turn, not just its final
    // message: a turn with tool calls spans several assistant
    // messages (one per API round), and reading only the last one
    // skips everything said before the final tool call. For each
    // turn-final index, gather the text of every assistant message
    // back to the previous user prompt.
    const turnTexts = new Map<number, { speak: string; copy: string }>()
    for (const k of turnFinalAssistant) {
      let start = k
      for (let n = k - 1; n >= 0; n--) {
        if (topLevelMessages[n].type === 'user_message') break
        start = n
      }
      const speakParts: string[] = []
      const copyParts: string[] = []
      for (let n = start; n <= k; n++) {
        const m = topLevelMessages[n]
        if (m.type !== 'assistant') continue
        const blocks = m.message?.content || []
        const s = extractSpeakableText(blocks)
        const c = extractCopyText(blocks)
        if (s) speakParts.push(s)
        if (c) copyParts.push(c)
      }
      turnTexts.set(k, { speak: speakParts.join('\n\n'), copy: copyParts.join('\n\n') })
    }

    // Listen reads the whole turn, which on a long turn is minutes of
    // narration before the part that says what actually happened. Map each
    // reading's speech id to its CLOSING message alone so the transport can
    // offer a jump straight there. Only turns where the two differ are listed —
    // for a single-message turn there is nothing to skip ahead to.
    const turnSummaries = new Map<string, string>()
    for (const k of turnFinalAssistant) {
      const m = topLevelMessages[k]
      if (m.type !== 'assistant') continue
      const finalText = extractSpeakableText(m.message?.content || [])
      const whole = turnTexts.get(k)?.speak
      if (!finalText || !whole || finalText === whole) continue
      // Must match the speechId the ListenButton registers under, or the bar
      // won't recognise the reading it's controlling.
      turnSummaries.set(m.message?.id || `assistant-${m.timestamp}`, finalText)
    }

    return { orphans, turnFinalAssistant, turnTexts, turnSummaries }
    // agentMeta intentionally excluded: description/agentType lookups for
    // orphans and inline subagent threads happen in the JSX below, off the
    // live `agentMeta` map — this memo only derives from message shape.
  }, [topLevelMessages, childrenByParent])

  const [modeMenuOpen, setModeMenuOpen] = useState(false)

  // Global keyboard shortcuts
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isWorking) {
        e.preventDefault()
        interrupt()
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [isWorking, interrupt])

  // Show login UI when bridge reports no credentials, login flow is active,
  // or the last result message indicates auth failure
  const needsLogin = useMemo(() => {
    // Active login flow (awaiting URL, code input, submitting)
    if (loginState.status !== 'idle' && loginState.status !== 'complete') return true
    // Bridge reported no credentials file
    if (loginState.error === 'not_authenticated') return true
    // Detect auth failure from the last result message (fallback for stale/expired credentials)
    if (messages.length > 0) {
      const last = messages[messages.length - 1]
      if (last.type === 'result' && last.data?.result && typeof last.data.result === 'string'
        && (last.data.result.includes('Not logged in') || last.data.result.includes('Please run /login'))) {
        return true
      }
    }
    return false
  }, [loginState, messages])

  // ── Hands-free voice conversation mode ────────────────────────────────────
  // Toggle in the composer: while ON, the agent's commentary is read aloud as
  // it arrives — intro, notes between tool calls, final summary — and the mic
  // listens for the reply once the turn is over. No taps in between.
  // The mic is only hot while nothing else is happening (agent idle, no
  // audio playing, no pending prompts), which also prevents the TTS output
  // from being transcribed back in as a feedback loop.
  const [voiceMode, setVoiceMode] = useState(false)
  const [voiceInterim, setVoiceInterim] = useState('')
  const voiceSpeech = useSpeech('voice-mode')

  const voiceCanListen = voiceMode && connected && cliConnected && !needsLogin
    && !isWorking && !voiceSpeech.speaking && !voiceSpeech.loading
    && pendingPermissions.length === 0 && pendingQuestions.length === 0

  // Quiet period before an accumulated spoken message auto-sends. The browser
  // endpointer finalizes after ~1.5s — too eager for someone composing a
  // thought — so segments buffer and only send after this much true quiet.
  const VOICE_DELAY_STEPS = [2000, 3500, 5000, 8000]
  const [voiceSendDelay, setVoiceSendDelay] = useState(() => {
    try {
      const stored = parseInt(localStorage.getItem('voice-send-delay') || '', 10)
      if (VOICE_DELAY_STEPS.includes(stored)) return stored
    } catch { /* ignore */ }
    return 3500
  })
  const cycleVoiceSendDelay = useCallback(() => {
    setVoiceSendDelay(prev => {
      const next = VOICE_DELAY_STEPS[(VOICE_DELAY_STEPS.indexOf(prev) + 1) % VOICE_DELAY_STEPS.length]
      try { localStorage.setItem('voice-send-delay', String(next)) } catch { /* ignore */ }
      return next
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleVoiceSend = useCallback((text: string) => {
    const trimmed = text.trim()
    if (!trimmed) return
    playEarcon('send')
    const hist = inputHistoryRef.current
    if (hist.length === 0 || hist[hist.length - 1] !== trimmed) {
      hist.push(trimmed)
      if (hist.length > 100) hist.shift()
    }
    isAtBottomRef.current = true
    sendMessage(trimmed)
    setStalled(false)
    if (stallTimerRef.current) clearTimeout(stallTimerRef.current)
    stallTimerRef.current = setTimeout(() => setStalled(true), 15000)
  }, [sendMessage])

  const voiceBuffer = useVoiceSendBuffer({ delayMs: voiceSendDelay, onSend: handleVoiceSend })
  const { pending: voicePending, addSegment: voiceAddSegment, hold: voiceHold } = voiceBuffer

  const handleVoiceInterim = useCallback((text: string) => {
    setVoiceInterim(text)
    // Speech in progress postpones the pending send — a thinking pause only
    // counts once the current utterance has fully gone quiet.
    if (text) voiceHold()
  }, [voiceHold])

  const { listening: voiceListening, micError, suspended: micSuspended } = useVoiceRecognition({
    active: voiceCanListen,
    onTranscript: voiceAddSegment,
    onInterim: handleVoiceInterim,
  })

  // Stage earcons for eyes-free (screen-locked) use: a rising two-tone when
  // it's the user's turn to speak, a low buzz on mic failure. (The send blip
  // lives in handleVoiceSend; the reply announces itself by speaking.)
  // micSuspended (screen locked — iOS denies the mic to hidden pages) maps to
  // 'idle', NOT 'listening': the recording ticks must never invite the user
  // to speak into a mic that is legally off. Playback still works locked; the
  // mic revives on unlock.
  const voicePhase = micError ? 'error'
    : (voiceSpeech.speaking || voiceSpeech.loading) ? 'speaking'
    : isWorking ? 'working'
    : (voiceCanListen && !micSuspended) ? 'listening'
    : 'idle'
  const prevVoicePhaseRef = useRef(voicePhase)
  // One spoken cue per turn when the reading finishes with the mic PARKED
  // (screen locked — iOS denies the mic to hidden pages): without it, an
  // eyes-free user waits for a listen earcon that will never come. Playback
  // works locked, so the cue itself is audible. Reset when a new turn starts.
  const unlockCueSpokenRef = useRef(false)
  useEffect(() => {
    if (isWorking) unlockCueSpokenRef.current = false
  }, [isWorking])
  useEffect(() => {
    const prev = prevVoicePhaseRef.current
    prevVoicePhaseRef.current = voicePhase
    if (!voiceMode || prev === voicePhase) return
    if (voicePhase === 'listening' && (prev === 'speaking' || prev === 'working')) playEarcon('listen')
    // The mic revived after an unlock cue (user unlocked): the listen earcon
    // confirms the mic is hot again — idle→listening plays no cue otherwise.
    if (voicePhase === 'listening' && prev === 'idle' && unlockCueSpokenRef.current) {
      unlockCueSpokenRef.current = false
      playEarcon('listen')
    }
    if (voicePhase === 'error') playEarcon('error')
    if (
      voicePhase === 'idle'
      && (prev === 'speaking' || prev === 'working')
      && micSuspended
      && !isWorking
      && !unlockCueSpokenRef.current
    ) {
      unlockCueSpokenRef.current = true
      voiceSpeech.speak('Unlock to reply.')
    }
  }, [voicePhase, voiceMode, micSuspended, isWorking, voiceSpeech])

  // Near-silent keep-alive loop for the whole voice session: it holds the
  // page's audio session open so iOS doesn't suspend the (locked-screen)
  // page between legs — the precondition for playback finishing and the mic
  // re-arming with the screen off.
  useEffect(() => {
    if (!voiceMode) return
    startVoiceKeepAlive()
    return () => stopVoiceKeepAlive()
  }, [voiceMode])

  // Ambient progress heartbeat, audible with the screen off: a soft low blip
  // while the agent works (or the reply is still synthesizing), quick light
  // ticks while the mic is recording. The reply's own voice covers the
  // speaking leg, so no loop runs then.
  useEffect(() => {
    if (!voiceMode) { stopProgressLoop(); return }
    if (voicePhase === 'working' || (voicePhase === 'speaking' && voiceSpeech.loading)) {
      startProgressLoop('working')
    } else if (voicePhase === 'listening') {
      startProgressLoop('recording')
    } else {
      stopProgressLoop()
    }
    return () => stopProgressLoop()
  }, [voiceMode, voicePhase, voiceSpeech.loading])

  // Streaming spoken commentary: each top-level assistant message is read
  // aloud as it COMPLETES — the intro before the first tool call, the short
  // notes between calls, and the final summary the moment it lands (before
  // the result frame even arrives). The hook queues completions and plays
  // them one at a time through the gesture-blessed voice element; it also
  // sweeps anything unspoken when the turn ends, so a turn cut short by an
  // interrupt still gets read.
  const voiceCommentary = useVoiceCommentary({
    enabled: voiceMode,
    messages: topLevelMessages,
    isWorking,
    speech: voiceSpeech,
  })

  const toggleVoiceMode = useCallback(() => {
    const next = !voiceMode
    if (next) {
      // The toggle click IS the user gesture — unlock all three voice-mode
      // elements (reply playback, keep-alive loop, earcons) for the
      // gesture-less audio that follows (iOS policy).
      blessAutoplayElement()
    } else {
      voiceCommentary.clear()
      voiceSpeech.stop()
      voiceBuffer.cancel()
      setVoiceInterim('')
    }
    setVoiceMode(next)
  }, [voiceMode, voiceSpeech, voiceBuffer, voiceCommentary])

  // Mirrors the bridge's agent registry (claude-bridge/agents/index.js).
  const AGENTS = useMemo(() => ([
    { value: 'claude', label: 'Claude', hint: 'Claude Code' },
    { value: 'codex', label: 'Codex', hint: 'OpenAI' },
  ]), [])
  const agent = agentKind
  // What the CURRENT agent can actually do, as reported by the bridge. Gating
  // on this (rather than on the agent name) keeps a control from being shown
  // for a CLI that would silently ignore it. Defaults are Claude's, so a
  // pre-capabilities bridge behaves exactly as before.
  const caps = (sessionState as { capabilities?: Record<string, boolean> })?.capabilities ?? {
    images: true, permissionMode: true, effort: true, conversationHistory: true, login: true,
  }
  const agentLabel = (sessionState as { agent_label?: string })?.agent_label
    || AGENTS.find(a => a.value === agent)?.label
    || 'Agent'

  const [agentMenuOpen, setAgentMenuOpen] = useState(false)
  const [modelMenuOpen, setModelMenuOpen] = useState(false)
  const [logoutMenuOpen, setLogoutMenuOpen] = useState(false)
  const [newChatMenuOpen, setNewChatMenuOpen] = useState(false)
  const [historyMenuOpen, setHistoryMenuOpen] = useState(false)

  const openHistoryMenu = useCallback(() => {
    // Side effect outside the setState updater (StrictMode runs updaters twice).
    if (!historyMenuOpen) listConversations() // refresh on open
    setHistoryMenuOpen(p => !p)
  }, [historyMenuOpen, listConversations])

  const handleResumeConversation = useCallback((id: string) => {
    resumeConversation(id)
    setHistoryMenuOpen(false)
  }, [resumeConversation])
  // Model picker entries. Preferred source: the CLI's own list_models catalog
  // relayed by the bridge (value, resolved id, display name, description) —
  // fully dynamic, tracks whatever models the subscription offers. The static
  // aliases are only the fallback until that catalog arrives (or against an
  // older bridge). `hint` shows what each row resolves to.
  const dynamicModels = sessionState?.models
  const MODELS = useMemo(() => {
    if (dynamicModels && dynamicModels.length > 0) {
      return dynamicModels.map(m => ({
        value: m.value,
        label: m.display_name || m.value,
        hint: m.resolved_model ? m.resolved_model.replace(/^claude-/, '') : '',
        description: m.description || '',
        resolved: m.resolved_model,
      }))
    }
    return [
      { value: 'fable', label: 'fable', hint: 'Fable 5', description: '', resolved: '' },
      { value: 'opus', label: 'opus', hint: 'Opus 5', description: '', resolved: '' },
      { value: 'sonnet', label: 'sonnet', hint: 'Sonnet 5', description: '', resolved: '' },
      { value: 'haiku', label: 'haiku', hint: 'Haiku 4.5', description: '', resolved: '' },
    ]
  }, [dynamicModels])

  // When sign-in is required the bridge deliberately does not launch the CLI,
  // so a perpetual "Starting Claude..." spinner would be a lie — say so.
  const statusIcon = connected
    ? (cliConnected
        ? <Wifi className="w-3 h-3 text-green-500" />
        : needsLogin
          ? <WifiOff className="w-3 h-3 text-yellow-500" />
          : <Loader2 className="w-3 h-3 text-yellow-500 animate-spin" />)
    : <WifiOff className="w-3 h-3 text-red-500" />

  // 'claude-sonnet-5-20250929' → 'sonnet-5'. Bare aliases pass through
  // (they only appear briefly, until the CLI reports the resolved id).
  const prettyModel = (m: string) =>
    m.replace(/^claude-/, '').replace(/\[1m\]$/, '').replace(/-20\d{6}$/, '')

  const statusText = connected
    ? (cliConnected ? (sessionState?.model ? prettyModel(sessionState.model) : 'Connected') : (needsLogin ? 'Sign-in required' : `Starting ${agentLabel}...`))
    : 'Disconnected'

  return (
    <TurnActiveContext.Provider value={isStreaming || isCompacting || activeTools.size > 0}>
    <Panel
      title="Chat"
      icon={<Bot className="w-4 h-4" />}
      onClose={onClose}
      closeable={closeable}
      isMaximized={isMaximized}
      onMaximize={onMaximize}
      leftExtra={
        /* Agent picker first, then the session actions. The panel itself is
           just "Chat" now — which agent is driving it is the user's choice,
           not the panel's identity. */
        connected && !needsLogin ? (
          <>
            <div className="relative">
              <button
                onClick={() => setAgentMenuOpen(p => !p)}
                className="flex items-center gap-1 px-1.5 py-1 rounded text-xs font-medium hover:bg-secondary/50 hover:text-foreground transition-colors"
                title="Choose which agent drives this chat"
                aria-label={`Agent: ${agentLabel}`}
              >
                <span>{agentLabel}</span>
                <ChevronDown className="w-3 h-3 flex-shrink-0" />
              </button>
              {agentMenuOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setAgentMenuOpen(false)} />
                  <div className="absolute left-0 top-full mt-1 z-50 rounded-lg border border-border bg-card shadow-lg py-1 min-w-[200px]">
                    {AGENTS.map(a => (
                      <button
                        key={a.value}
                        onClick={() => {
                          if (a.value !== agent) { setAgent(a.value); setAgentKind(a.value) }
                          setAgentMenuOpen(false)
                        }}
                        className={`w-full text-left px-3 py-1.5 text-xs hover:bg-secondary/50 transition-colors ${
                          a.value === agent ? 'text-primary font-medium' : ''
                        }`}
                      >
                        <span className="flex items-center justify-between gap-3">
                          <span>{a.label}</span>
                          <span className="text-muted-foreground/70 font-normal">{a.hint}</span>
                        </span>
                      </button>
                    ))}
                    <p className="px-3 pt-1.5 pb-1 text-[10px] leading-snug text-muted-foreground/70 border-t border-border mt-1">
                      Switching starts a new conversation — the agents keep
                      separate histories.
                    </p>
                  </div>
                </>
              )}
            </div>
            <span className="text-border">·</span>
            {/* New chat — archives current session, clears feed */}
            <div className="relative">
              <button
                onClick={() => setNewChatMenuOpen(p => !p)}
                className="flex items-center p-1 rounded hover:bg-secondary/50 hover:text-foreground transition-colors"
                title="New chat"
                aria-label="Start a new chat"
              >
                <MessageSquarePlus className="w-3.5 h-3.5" />
              </button>
              {newChatMenuOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setNewChatMenuOpen(false)} />
                  <div className="absolute left-0 top-full mt-1 z-50 rounded-lg border border-border bg-card shadow-lg p-3 w-60 space-y-2">
                    <p className="text-xs text-foreground">Start a new chat?</p>
                    <p className="text-xs text-muted-foreground">
                      The current conversation will be archived.
                      {caps.conversationHistory ? (
                        <>
                          {' '}You can recover it from
                          <code className="mx-1 px-1 rounded bg-secondary/50 text-[10px]">~/.claude/projects/</code>
                          if needed.
                        </>
                      ) : null}
                    </p>
                    <div className="flex justify-end gap-2 pt-1">
                      <button
                        onClick={() => setNewChatMenuOpen(false)}
                        className="px-2 py-1 rounded text-xs hover:bg-secondary/50 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => { newSession(); setNewChatMenuOpen(false) }}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded bg-primary text-primary-foreground text-xs hover:bg-primary/90 transition-colors"
                      >
                        <MessageSquarePlus className="w-3 h-3" />
                        New chat
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
            {/* Conversation history — resume any past conversation (Claude-web
                style). Listed from the CLI's own transcripts, so chats that
                happened in the terminal show up too. Only for agents whose
                transcripts the bridge can render. */}
            {caps.conversationHistory && (
            <div className="relative">
              <button
                onClick={openHistoryMenu}
                className="flex items-center p-1 rounded hover:bg-secondary/50 hover:text-foreground transition-colors"
                title="Conversation history"
                aria-label="Open conversation history"
              >
                <History className="w-3.5 h-3.5" />
              </button>
              {historyMenuOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setHistoryMenuOpen(false)} />
                  <div className="absolute left-0 top-full mt-1 z-50 rounded-lg border border-border bg-card shadow-lg py-1 w-72 max-h-80 overflow-y-auto scrollbar-thin">
                    <p className="px-3 py-1.5 text-[10px] uppercase tracking-wide text-muted-foreground">Conversations</p>
                    {conversations.length === 0 && (
                      <p className="px-3 py-2 text-xs text-muted-foreground">No past conversations found.</p>
                    )}
                    {conversations.map(c => (
                      <button
                        key={c.id}
                        onClick={() => handleResumeConversation(c.id)}
                        disabled={c.active}
                        className={`w-full text-left px-3 py-2 hover:bg-secondary/50 transition-colors ${
                          c.active ? 'cursor-default bg-secondary/30' : ''
                        }`}
                      >
                        <span className="flex items-center gap-1.5">
                          <span className="flex-1 min-w-0 truncate text-xs text-foreground">
                            {c.title || 'Untitled conversation'}
                          </span>
                          {c.active && (
                            <span className="flex-shrink-0 text-[10px] px-1 rounded bg-primary/15 text-primary">Current</span>
                          )}
                          {c.archived && !c.active && (
                            <Archive className="flex-shrink-0 w-3 h-3 text-muted-foreground" aria-label="Archived" />
                          )}
                        </span>
                        <span className="block text-[10px] text-muted-foreground">
                          {Number.isFinite(c.updated_at) ? formatRelativeTime(new Date(c.updated_at).toISOString()) : ''}
                        </span>
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
            )}
            {/* Sign out — wipes stored credentials so the user can force re-auth
                when Anthropic returns a stale 401 and the CLI can't recover.
                Agents whose credentials the platform provisions have no
                in-panel sign-in to undo. */}
            {caps.login && (
            <div className="relative">
              <button
                onClick={() => setLogoutMenuOpen(p => !p)}
                className="flex items-center p-1 rounded hover:bg-secondary/50 hover:text-foreground transition-colors"
                title={`Sign out of ${agentLabel}`}
                aria-label={`Sign out of ${agentLabel}`}
              >
                <LogOut className="w-3.5 h-3.5" />
              </button>
              {logoutMenuOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setLogoutMenuOpen(false)} />
                  <div className="absolute left-0 top-full mt-1 z-50 rounded-lg border border-border bg-card shadow-lg p-3 w-56 space-y-2">
                    <p className="text-xs text-foreground">Sign out of Claude?</p>
                    <p className="text-xs text-muted-foreground">
                      This removes stored credentials. You'll need to sign in again to continue.
                    </p>
                    <div className="flex justify-end gap-2 pt-1">
                      <button
                        onClick={() => setLogoutMenuOpen(false)}
                        className="px-2 py-1 rounded text-xs hover:bg-secondary/50 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => { logout(); setLogoutMenuOpen(false) }}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded bg-destructive text-destructive-foreground text-xs hover:bg-destructive/90 transition-colors"
                      >
                        <LogOut className="w-3 h-3" />
                        Sign out
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
            )}
          </>
        ) : null
      }
      extra={
        <div className="flex items-center gap-1.5 min-w-0 text-xs text-muted-foreground">
          {isCompacting && (
            <span className="flex items-center gap-1 text-yellow-500">
              <Loader2 className="w-3 h-3 animate-spin" />
              Compacting
            </span>
          )}
          {statusIcon}
          {/* Model switcher dropdown */}
          {cliConnected && sessionState?.model ? (
            <div className="relative">
              <button
                onClick={() => setModelMenuOpen(p => !p)}
                className="flex items-center gap-0.5 min-w-0 hover:text-foreground transition-colors"
              >
                {/* Truncate on phones so the model picker never gets pushed out
                    of the crowded header — the dropdown shows full names. */}
                <span className="truncate max-w-24 sm:max-w-none">{statusText}</span>
                <ChevronDown className="w-3 h-3 flex-shrink-0" />
              </button>
              {modelMenuOpen && MODELS.length > 0 && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setModelMenuOpen(false)} />
                  <div className="absolute right-0 top-full mt-1 z-50 rounded-lg border border-border bg-card shadow-lg py-1 min-w-[180px] max-w-[280px]">
                    {MODELS.map(m => {
                      const current = sessionState.model
                      const isActive = !!current && (
                        m.value === current
                        || (m.resolved && m.resolved.replace(/\[1m\]$/, '') === current)
                        || (!m.resolved && current.includes(m.value))
                      )
                      return (
                        <button
                          key={m.value}
                          onClick={() => { setModel(m.value); setModelMenuOpen(false) }}
                          title={m.description || undefined}
                          className={`w-full text-left px-3 py-1.5 text-xs hover:bg-secondary/50 transition-colors ${
                            isActive ? 'text-primary font-medium' : ''
                          }`}
                        >
                          <span className="flex items-center justify-between gap-3">
                            <span>{m.label}</span>
                            <span className="text-muted-foreground/70 font-normal truncate">{m.hint}</span>
                          </span>
                          {m.description && (
                            <span className="block text-[10px] text-muted-foreground/70 font-normal truncate">
                              {m.description}
                            </span>
                          )}
                        </button>
                      )
                    })}
                  </div>
                </>
              )}
            </div>
          ) : (
            <span>{statusText}</span>
          )}
          {/* Permission mode dropdown — only for agents that can act on it. */}
          {cliConnected && caps.permissionMode && (
            <>
              <span className="text-border hidden sm:inline">·</span>
              <div className="relative">
                <button
                  onClick={() => setModeMenuOpen(p => !p)}
                  className="flex items-center gap-0.5 hover:text-foreground transition-colors"
                >
                  <Shield className="w-3 h-3 flex-shrink-0" />
                  <span className="hidden sm:inline">{MODE_LABELS[currentMode] || currentMode}</span>
                  <ChevronDown className="w-3 h-3 flex-shrink-0" />
                </button>
                {modeMenuOpen && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setModeMenuOpen(false)} />
                    <div className="absolute right-0 top-full mt-1 z-50 rounded-lg border border-border bg-card shadow-lg py-1 min-w-[140px]">
                      {PERMISSION_MODES.map(m => (
                        <button
                          key={m}
                          onClick={() => { setPermissionMode(m); setModeMenuOpen(false) }}
                          className={`w-full text-left px-3 py-1.5 text-xs hover:bg-secondary/50 transition-colors ${
                            currentMode === m ? 'text-primary font-medium' : ''
                          }`}
                        >
                          {MODE_LABELS[m]}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </>
          )}
          {/* Hand the main pane back to the terminal CLI. The switch flow tells
              the platform to release this panel's CLI so the terminal's
              `claude --continue` picks the conversation up seamlessly. */}
          {onSwitchToTerminal && (
            <>
              <span className="text-border hidden sm:inline">·</span>
              <button
                onClick={onSwitchToTerminal}
                className="flex items-center gap-1 hover:text-foreground transition-colors"
                title="Switch to the terminal"
                aria-label="Switch to the terminal"
              >
                <Terminal className="w-3 h-3 flex-shrink-0" />
                <span className="hidden sm:inline">Terminal</span>
              </button>
            </>
          )}
        </div>
      }
    >
      <div className="flex flex-col h-full bg-background">
        {/* Connection status banners. The reconnect banner is suppressed while
            sign-in is required: the bridge intentionally won't relaunch the CLI
            then, so "reconnecting..." would never come true — the LoginCard is
            the actionable surface instead. */}
        {connected && !cliConnected && !needsLogin && (
          <div className="flex items-center gap-2 px-3 py-1.5 text-xs bg-yellow-500/10 text-yellow-600 dark:text-yellow-400 border-b border-yellow-500/20">
            <Loader2 className="w-3 h-3 animate-spin" />
            <span>Claude CLI disconnected — reconnecting...</span>
          </div>
        )}
        {!connected && (
          // While the device is offline, "Reconnecting…" with a spinner is a
          // promise the app can't keep — the socket cannot come back until the
          // network does. Name the actual blocker instead, and drop the spinner
          // so it doesn't imply progress.
          offline ? (
            <div className="flex items-center gap-2 border-b border-border bg-secondary/60 px-3 py-1.5 text-xs text-muted-foreground">
              <WifiOff className="h-3 w-3 flex-shrink-0" aria-hidden="true" />
              <span>You&apos;re offline — {agentLabel} reconnects automatically when you&apos;re back.</span>
            </div>
          ) : (
            <div className="flex items-center justify-between px-3 py-1.5 text-xs bg-yellow-500/10 text-yellow-600 dark:text-yellow-400 border-b border-yellow-500/20">
              <div className="flex items-center gap-2">
                <Loader2 className="w-3 h-3 animate-spin" />
                <span>Reconnecting...</span>
              </div>
            </div>
          )
        )}

        {/* Message Feed */}
        <div
          ref={messageFeedRef}
          data-vv-feed
          onScroll={handleMessageFeedScroll}
          // overscroll-contain prevents the iOS rubber-band gesture from
          // chaining out to the body/page scroller when the user reaches the
          // top/bottom of the chat. Without it, momentum scrolling at the
          // chat's limits drags the entire workspace layout up, hiding the
          // outer tab bar (Claude/Terminal/Preview/Files/Docs/Git).
          className="flex-1 min-w-0 overflow-x-hidden overflow-y-auto overscroll-contain p-3 space-y-3 scrollbar-thin"
        >
          {/* Welcome Banner — shown before any messages */}
          {messages.length === 0 && !needsLogin && (
            <WelcomeBanner
              cliConnected={cliConnected}
              sessionState={sessionState}
            />
          )}

          {/* Login card — shown prominently when no messages and auth needed */}
          {messages.length === 0 && needsLogin && (
            <div className="flex flex-col items-center justify-center h-full gap-4">
              <Bot className="w-10 h-10 text-primary/40" />
              <LoginCard
                loginState={loginState}
                onStartLogin={startLogin}
                onSubmitCode={submitLoginCode}
              />
            </div>
          )}

          {(() => {
            const { orphans, turnFinalAssistant, turnTexts } = feedComputed

            // Cursor into the orphan list — we walk through top-level
            // messages and emit each orphan as soon as its timestamp falls
            // before the next message. This positions a "lost-parent"
            // subagent at the chronological spot it actually occurred at,
            // rather than dumping all of them at the bottom of the feed.
            let orphanIdx = 0
            const renderOrphan = (o: Orphan) => {
              const info = agentMeta.get(o.parentId)
              return (
                <SubagentThread
                  key={`orphan-${o.parentId}`}
                  description={info?.description || 'Subagent'}
                  agentType={info?.agentType || ''}
                  messages={o.kids}
                  childrenByParent={childrenByParent}
                  agentMeta={agentMeta}
                  isStreaming={isStreaming}
                />
              )
            }
            const orphansBefore = (boundaryTs: number) => {
              const out = []
              while (orphanIdx < orphans.length && orphans[orphanIdx].timestamp < boundaryTs) {
                out.push(renderOrphan(orphans[orphanIdx]))
                orphanIdx++
              }
              return out
            }
            const msgTs = (m: ChatMessage): number =>
              ('timestamp' in m ? (m as { timestamp?: number }).timestamp ?? 0 : 0)

            return (
              <>
                {topLevelMessages.map((msg, i) => {
                  const isLast = i === topLevelMessages.length - 1
                  // Emit any orphans that happened before this top-level
                  // message. On the first iteration this catches orphans
                  // that predate the entire visible thread (rare but
                  // possible after history compaction).
                  const leadingOrphans = orphansBefore(msgTs(msg))
                  return (
                    <div key={i}>
                      {leadingOrphans}
                      <MessageBubble
                        message={msg}
                        isLast={isLast}
                        isFinalReply={turnFinalAssistant.has(i)}
                        isStreaming={isStreaming}
                        turnSpeakable={turnTexts.get(i)?.speak}
                        turnCopyable={turnTexts.get(i)?.copy}
                      />
                      {/* Render subagent threads for Agent/Task tool_use in this message */}
                      {msg.type === 'assistant' && (msg.message?.content || []).map(block => {
                        if (block.type !== 'tool_use' || !block.id) return null
                        if (block.name !== 'Agent' && block.name !== 'Task') return null
                        const kids = childrenByParent.get(block.id)
                        if (!kids || kids.length === 0) return null
                        const info = agentMeta.get(block.id)
                        return (
                          <SubagentThread
                            key={block.id}
                            description={info?.description || 'Subagent'}
                            agentType={info?.agentType || ''}
                            messages={kids}
                            childrenByParent={childrenByParent}
                            agentMeta={agentMeta}
                            isStreaming={isStreaming}
                          />
                        )
                      })}
                    </div>
                  )
                })}
                {/* Any remaining orphans (timestamp after the last top-level
                    message, or no top-level messages at all) trail at the
                    end — same as before, just for the residual tail. */}
                {orphans.slice(orphanIdx).map(renderOrphan)}
              </>
            )
          })()}

          {/* Streaming bubble — only show when no stored assistant message has the content yet.
              Once a non-final assistant message is stored (via --include-partial-messages),
              the stored message already renders the text, so we don't need a separate bubble. */}
          {(() => {
            const lastMsg = messages[messages.length - 1]
            const lastIsLiveAssistant = lastMsg?.type === 'assistant'
              && (lastMsg as { message?: { stop_reason?: string | null } }).message?.stop_reason == null
            return isStreaming && (streamingText || streamingThinking) && !lastIsLiveAssistant
          })() && (
            <div className="space-y-2">
              <div>
                {streamingThinking && (
                  <details className="group text-xs text-muted-foreground" open>
                    <summary className="flex items-center gap-1.5 cursor-pointer hover:text-foreground select-none">
                      <ChevronRight className="w-3 h-3 transition-transform group-open:rotate-90" />
                      Thinking...
                    </summary>
                    <pre className="mt-1 whitespace-pre-wrap">{streamingThinking}</pre>
                  </details>
                )}
                {smoothText && (
                  <div className="prose dark:prose-invert max-w-none break-words prose-pre:bg-muted prose-pre:border prose-pre:border-border prose-code:text-foreground prose-code:before:content-none prose-code:after:content-none prose-p:my-1.5 prose-headings:my-2 prose-ul:my-1.5 prose-ol:my-1.5 prose-li:my-0.5 prose-a:break-words">
                    <MarkdownView text={smoothText} />
                    <span className="inline-block w-0.5 h-4 bg-primary animate-pulse align-text-bottom ml-0.5" />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Working indicator — pulsing dots when Claude is processing but no streaming text visible */}
          {isWorking && !(streamingText || streamingThinking) && !isCompacting && activeTools.size === 0 && messages.length > 0 && (
            <div className="flex justify-start">
              <div className="flex items-center gap-1.5 px-3 py-2">
                <span className="flex gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary/60 animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-1.5 h-1.5 rounded-full bg-primary/60 animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="w-1.5 h-1.5 rounded-full bg-primary/60 animate-bounce" style={{ animationDelay: '300ms' }} />
                </span>
              </div>
            </div>
          )}

          {/* Stall warning — no response after 15s */}
          {stalled && (
            <div className="rounded-lg border border-yellow-500/30 bg-yellow-500/10 px-3 py-2 text-sm space-y-1">
              <p className="text-yellow-600 dark:text-yellow-400">
                Response is taking longer than usual.
              </p>
              <p className="text-xs text-muted-foreground">
                Claude may be processing a complex request, or Anthropic may be experiencing issues.{' '}
                <a href="https://status.anthropic.com" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                  Check Anthropic Status
                </a>
              </p>
            </div>
          )}

          {/* Pending Permission Requests */}
          {pendingPermissions.map((perm) => (
            <PermissionCard
              key={perm.request_id}
              request={perm}
              onRespond={respondToPermission}
            />
          ))}

          {/* Pending Questions (AskUserQuestion) */}
          {pendingQuestions.map((q) => (
            <QuestionCard
              key={q.request_id}
              request={q}
              onRespond={respondToQuestion}
            />
          ))}

          {/* Login Card — inline when there are already messages */}
          {messages.length > 0 && needsLogin && (
            <LoginCard
              loginState={loginState}
              onStartLogin={startLogin}
              onSubmitCode={submitLoginCode}
            />
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Tool Progress + Input Area */}
        <div className="border-t border-border">
          {/* Active tool progress bar */}
          {activeTools.size > 0 && (
            <ToolProgressBar activeTools={activeTools} />
          )}

          {/* Bottom padding ABSORBS the iOS home-indicator inset rather than
              stacking on top of it, and the vertical padding is tighter than
              the old uniform p-3 — on a phone every row of chat above the
              composer is worth more than symmetric whitespace around it. */}
          <div className="px-3 pt-2 pb-[max(0.5rem,env(safe-area-inset-bottom))]">
            {/* Slash Command Menu */}
            {slashMenuVisible && (
              <div
                ref={slashMenuRef}
                className="mb-2 max-h-48 overflow-y-auto rounded-lg border border-border bg-card shadow-lg scrollbar-thin"
              >
                {filteredCommands.map((cmd, i) => (
                  <button
                    key={cmd.name}
                    data-cmd-index={i}
                    onClick={() => selectCommand(cmd)}
                    className={`w-full text-left px-3 py-1.5 min-w-0 text-sm transition-colors ${
                      i === slashMenuIndex ? 'bg-primary/10 text-primary' : 'hover:bg-secondary/50'
                    }`}
                  >
                    {/* Narrowed-down list (few matches): give the description
                        room to wrap so the user can actually read it before
                        committing. Long lists stay one compact line each. */}
                    {filteredCommands.length <= 3 && cmd.description ? (
                      <span className="flex flex-col gap-0.5 min-w-0">
                        <span className="flex items-center gap-2">
                          <Slash className="w-3 h-3 text-muted-foreground shrink-0" />
                          <span className="font-medium">{cmd.name}</span>
                        </span>
                        <span className="text-xs text-muted-foreground whitespace-normal line-clamp-3 pl-5">
                          {cmd.description}
                        </span>
                      </span>
                    ) : (
                      <span className="flex items-center gap-2 min-w-0">
                        <Slash className="w-3 h-3 text-muted-foreground shrink-0" />
                        <span className="font-medium shrink-0">{cmd.name}</span>
                        <span className="text-xs text-muted-foreground ml-auto truncate text-right">
                          {cmd.description || cmd.type}
                        </span>
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}
            {/* Argument hint for the picked command — same frontmatter the CLI
                shows in its own autocomplete. */}
            {activeCommandHint && !slashMenuVisible && (
              <div className="mb-2 min-w-0 px-3 py-1.5 rounded-lg border border-border/50 bg-secondary/30 text-xs text-muted-foreground">
                <span className="flex items-center gap-2">
                  <Slash className="w-3 h-3 shrink-0" />
                  <span className="font-medium text-foreground shrink-0">/{activeCommandHint.name}</span>
                  {activeCommandHint.argument_hint && (
                    <span className="truncate">{activeCommandHint.argument_hint}</span>
                  )}
                </span>
                {activeCommandHint.description && (
                  <span className="block whitespace-normal line-clamp-2 pl-5 mt-0.5">
                    {activeCommandHint.description}
                  </span>
                )}
              </div>
            )}
            {/* Image thumbnails */}
            {images.length > 0 && (
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                {images.map((img, i) => (
                  <div key={i} className="relative group">
                    <img
                      src={`data:${img.media_type};base64,${img.data}`}
                      alt={`Attachment ${i + 1}`}
                      className="w-12 h-12 rounded-lg object-cover border border-border"
                    />
                    <button
                      onClick={() => setImages(prev => prev.filter((_, j) => j !== i))}
                      aria-label="Remove image"
                      className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center text-[10px] opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="w-2.5 h-2.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handleFileSelect}
              className="hidden"
            />
            {/* Playback transport, pinned to the composer rather than to the
                message it came from: the reading outlives the bubble scrolling
                out of view, and hunting back up the feed to pause something is
                the whole complaint this answers. */}
            <AudioTransportBar summaries={feedComputed.turnSummaries} />
            {/* Voice-mode status strip — tells the user which leg of the
                hands-free loop is running (listening / working / reading),
                plus the accumulated not-yet-sent message with its controls. */}
            {voiceMode && (
              <div className="mb-2 min-w-0 px-3 py-1.5 rounded-lg border border-primary/30 bg-primary/5 text-xs text-muted-foreground space-y-1">
                <div className="flex items-center gap-2 min-w-0">
                  {micError ? (
                    <span className="text-destructive">Voice input unavailable — {micError}.</span>
                  ) : voiceSpeech.speaking || voiceSpeech.loading ? (
                    <>
                      <Volume2 className="w-3.5 h-3.5 text-primary shrink-0" />
                      <span className="shrink-0">{isWorking ? 'Reading update…' : 'Reading reply…'}</span>
                      {/* Skips only the CURRENT reading — queued commentary
                          that follows still plays (that content is new). */}
                      <button
                        type="button"
                        onClick={() => voiceSpeech.stop()}
                        className="underline hover:text-foreground shrink-0"
                      >
                        skip
                      </button>
                    </>
                  ) : isWorking ? (
                    <>
                      <Loader2 className="w-3.5 h-3.5 animate-spin shrink-0" />
                      <span>Claude is working…</span>
                    </>
                  ) : voiceListening ? (
                    <>
                      <Mic className="w-3.5 h-3.5 text-primary animate-pulse shrink-0" />
                      <span className="shrink-0">
                        {voicePending ? 'Keep talking to add more…' : 'Listening — speak whenever.'}
                      </span>
                      {voiceInterim && (
                        <span className="italic text-foreground/80 truncate">{voiceInterim}</span>
                      )}
                    </>
                  ) : micSuspended ? (
                    <>
                      <Mic className="w-3.5 h-3.5 shrink-0" />
                      <span>Microphone paused while the screen is off — unlock to speak.</span>
                    </>
                  ) : (
                    <>
                      <Mic className="w-3.5 h-3.5 shrink-0" />
                      <span>Voice mode on — waiting for the microphone…</span>
                    </>
                  )}
                  <button
                    type="button"
                    onClick={cycleVoiceSendDelay}
                    title="Quiet time before your spoken message auto-sends (tap to change)"
                    className="ml-auto shrink-0 px-1.5 py-0.5 rounded border border-border/60 text-[10px] tabular-nums hover:text-foreground hover:border-border transition-colors"
                  >
                    {(voiceSendDelay / 1000).toString().replace(/\.0$/, '')}s pause
                  </button>
                </div>
                {voicePending && !isWorking && (
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="flex-1 min-w-0 truncate text-foreground/90">“{voicePending}”</span>
                    <button
                      type="button"
                      onClick={voiceBuffer.sendNow}
                      className="shrink-0 px-1.5 py-0.5 rounded bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
                    >
                      Send now
                    </button>
                    <button
                      type="button"
                      onClick={voiceBuffer.cancel}
                      title="Discard this spoken message"
                      className="shrink-0 underline hover:text-foreground"
                    >
                      discard
                    </button>
                  </div>
                )}
              </div>
            )}
            <form onSubmit={handleSubmit} className="flex items-end gap-2">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={!connected || !caps.images}
                className="flex items-center justify-center w-9 h-9 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary/50 disabled:opacity-50 transition-colors shrink-0"
                title={caps.images ? 'Attach image' : `${agentLabel} can't receive image attachments`}
              >
                <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-4 h-4">
                  <path d="M8 3v10M3 8h10" strokeLinecap="round" />
                </svg>
              </button>
              <button
                type="button"
                onClick={toggleVoiceMode}
                disabled={!connected}
                className={`flex items-center justify-center w-9 h-9 rounded-lg disabled:opacity-50 transition-colors shrink-0 ${
                  voiceMode
                    ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                    : 'text-muted-foreground hover:text-foreground hover:bg-secondary/50'
                }`}
                title={voiceMode
                  ? 'Turn off voice conversation mode'
                  : 'Voice conversation — replies are read aloud and the mic listens for yours'}
                aria-label={voiceMode ? 'Turn off voice conversation mode' : 'Turn on voice conversation mode'}
                aria-pressed={voiceMode}
              >
                <AudioLines className="w-4 h-4" />
              </button>
              <textarea
                ref={textareaRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onInput={handleTextareaInput}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                placeholder={connected ? 'Message Claude...' : 'Connecting...'}
                disabled={!connected}
                rows={1}
                className="cc-focus-blink flex-1 resize-none rounded-lg border border-border bg-card px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50 min-h-[38px] max-h-[150px]"
              />
              {isWorking ? (
                <button
                  type="button"
                  onClick={interrupt}
                  className="flex items-center justify-center w-9 h-9 rounded-lg bg-destructive text-destructive-foreground hover:bg-destructive/90 transition-colors"
                  title={`Interrupt ${agentLabel}`}
                >
                  <Square className="w-4 h-4" />
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={!connected || !input.trim()}
                  className="flex items-center justify-center w-9 h-9 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 transition-colors"
                  title="Send message"
                >
                  <Send className="w-4 h-4" />
                </button>
              )}
            </form>
          </div>
        </div>
      </div>
    </Panel>
    </TurnActiveContext.Provider>
  )
}

// ─── Tool Progress Bar ───────────────────────────────────────────────────────

function ToolProgressBar({ activeTools }: { activeTools: Map<string, ToolProgress> }) {
  // Group by tool name, show max elapsed per tool
  const toolSummaries = useMemo(() => {
    const byName = new Map<string, number>()
    for (const [, { toolName, elapsed }] of activeTools) {
      const current = byName.get(toolName) || 0
      if (elapsed > current) byName.set(toolName, elapsed)
    }
    return Array.from(byName.entries())
  }, [activeTools])

  return (
    <div className="flex items-center gap-2 px-3 py-1.5 text-xs text-muted-foreground bg-secondary/20">
      <span className="relative flex h-2 w-2 shrink-0">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary/60" />
        <span className="relative inline-flex rounded-full h-2 w-2 bg-primary" />
      </span>
      {toolSummaries.map(([name, elapsed], i) => (
        <span key={name}>
          {i > 0 && <span className="text-border mx-1">&middot;</span>}
          <span className="font-medium">{name}</span>
          <span className="ml-1">{Math.round(elapsed)}s</span>
        </span>
      ))}
    </div>
  )
}

// ─── Welcome Banner ─────────────────────────────────────────────────────────

function WelcomeBanner({
  cliConnected,
  sessionState,
}: {
  cliConnected: boolean
  sessionState: { claude_code_version?: string; model?: string; cwd?: string; permissionMode?: string } | null
}) {
  if (!cliConnected || !sessionState) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
        <Bot className="w-10 h-10 mb-3 opacity-40" />
        <p className="text-sm">Connecting to Claude...</p>
      </div>
    )
  }

  const rawVersion = sessionState.claude_code_version || ''
  // Strip "Claude Code" prefix and parens wrapper: "2.1.72 (Claude Code)" → "v2.1.72"
  const version = rawVersion.replace(/\s*\(Claude Code\)/i, '').replace(/^Claude Code\s*/i, '').trim()
  const model = sessionState.model || ''
  const cwd = sessionState.cwd || '~'
  // Shorten home dir paths
  const displayCwd = cwd.replace(/^\/home\/[^/]+/, '~').replace(/^\/Users\/[^/]+/, '~')

  return (
    <div className="flex flex-col items-center justify-center h-full gap-4 select-none">
      <div className="flex flex-col items-center gap-2">
        <Terminal className="w-8 h-8 text-primary/70" />
        <div className="text-center space-y-1">
          <p className="text-sm font-semibold text-foreground">
            Claude Code{version ? ` v${version.replace(/^v/i, '')}` : ''}
          </p>
          {model && <p className="text-xs text-muted-foreground">{model}</p>}
        </div>
      </div>
      <div className="text-xs text-muted-foreground/60 font-mono">{displayCwd}</div>
      <p className="text-xs text-muted-foreground max-w-xs text-center">
        Type a message below to start working, or use <kbd className="px-1 py-0.5 rounded bg-secondary text-foreground font-mono text-[10px]">/</kbd> for slash commands.
      </p>
    </div>
  )
}


function CopyMessageButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false)

  const handleCopy = useCallback(async () => {
    if (!text) return
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text)
      } else {
        const ta = document.createElement('textarea')
        ta.value = text
        ta.style.position = 'fixed'
        ta.style.opacity = '0'
        document.body.appendChild(ta)
        ta.select()
        document.execCommand('copy')
        document.body.removeChild(ta)
      }
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch (err) {
      console.error('[claude-panel] copy failed:', err)
    }
  }, [text])

  return (
    <button
      type="button"
      onClick={handleCopy}
      aria-label={copied ? 'Copied' : 'Copy reply'}
      title={copied ? 'Copied' : 'Copy reply'}
      className="inline-flex items-center justify-center h-6 w-6 rounded text-muted-foreground/60 hover:text-foreground hover:bg-secondary/40 transition-colors"
    >
      {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
    </button>
  )
}

// ─── Message Bubble Component ───────────────────────────────────────────────

// Memoized: typing into the input triggers a parent re-render every keystroke.
// Before memo, every bubble re-rendered (and re-parsed its markdown) on every
// keystroke, so lag grew with chat length. With memo, an unchanged message ref
// makes the bubble bail out of reconciliation entirely. `isLast`/`isStreaming`
// are stable primitives across keystrokes, so they don't bust shallow equality.
// True while Claude is actively working — streaming a message, running a tool,
// or compacting. Copy/Listen live on each turn's final reply and persist there;
// this flag only suppresses them on the reply being written RIGHT NOW (the last
// message while the turn is active), so a half-written mid-turn snapshot is never
// copyable/speakable. We key off "actively working now" rather than "turn fully
// complete" on purpose: it can't get stuck hiding the buttons on a restored/idle
// session (whose last message may have no stop_reason).
const TurnActiveContext = createContext(false)

// Backfilled user-ROLE lines the user did not type — harness task
// notifications and compaction summaries. They must stay readable (users do
// open them), but rendered as a muted, collapsed, left-aligned system card so
// they never read as the user's own words in their own bubble.
function SystemNoteCard({ kind, content }: { kind: 'system-note' | 'compaction'; content: string }) {
  const [open, setOpen] = useState(false)
  const label = kind === 'compaction'
    ? 'Conversation compacted — earlier turns were summarized'
    : 'System notification'
  return (
    <div className="flex justify-start min-w-0">
      <div className="max-w-[85%] min-w-0 rounded-lg border border-border/60 bg-muted/30 text-muted-foreground text-xs">
        <button
          type="button"
          onClick={() => setOpen(p => !p)}
          className="w-full flex items-center gap-1.5 px-3 py-1.5 hover:text-foreground transition-colors"
        >
          {open ? <ChevronDown className="w-3 h-3 shrink-0" /> : <ChevronRight className="w-3 h-3 shrink-0" />}
          <span className="truncate">{label}</span>
        </button>
        {open && (
          <pre className="px-3 pb-2 whitespace-pre-wrap break-words font-sans max-h-64 overflow-y-auto">{content}</pre>
        )}
      </div>
    </div>
  )
}

const MessageBubble = memo(function MessageBubble({ message, isLast, isFinalReply, isStreaming, turnSpeakable, turnCopyable }: { message: ChatMessage; isLast?: boolean; isFinalReply?: boolean; isStreaming?: boolean; turnSpeakable?: string; turnCopyable?: string }) {
  const turnActive = useContext(TurnActiveContext)
  if (message.type === 'user_message') {
    if (message.kind === 'system-note' || message.kind === 'compaction') {
      return <SystemNoteCard kind={message.kind} content={message.content} />
    }
    const msgImages = message.images
    return (
      <div className="flex justify-end min-w-0">
        <div className="max-w-[85%] min-w-0 rounded-lg px-3 py-2 bg-primary/10 text-foreground space-y-2">
          {msgImages && msgImages.length > 0 && (
            <div className="space-y-1.5">
              {msgImages.map((img, i) => (
                <ChatImage
                  key={i}
                  src={`data:${img.media_type};base64,${img.data}`}
                  alt={`Attachment ${i + 1}`}
                />
              ))}
            </div>
          )}
          <pre className="whitespace-pre-wrap break-words font-sans">{message.content}</pre>
        </div>
      </div>
    )
  }

  if (message.type === 'assistant') {
    const content = message.message?.content || []
    const grouped = groupContentBlocks(content)
    const isFinal = message.message?.stop_reason != null
    const showCursor = isLast && isStreaming && !isFinal
    // Prefer the turn-wide text (all assistant messages since the user's
    // prompt) so Listen/Copy cover the whole reply, not just the segment
    // after the last tool call.
    const speakable = turnSpeakable || extractSpeakableText(content)
    const copyable = turnCopyable || extractCopyText(content)
    const speechId = message.message?.id || `assistant-${message.timestamp}`
    return (
      <div className="space-y-2">
        {grouped.map((group, i) => (
          <ContentGroupView key={i} group={group} />
        ))}
        {showCursor && (
          <span className="inline-block w-0.5 h-4 bg-primary animate-pulse align-text-bottom ml-0.5" />
        )}
        {/* Show Copy/Listen on the final reply of each turn (isFinalReply), and
            keep them there — replying starts a new turn but the earlier summary
            keeps its actions. Only the reply that is being written RIGHT NOW is
            suppressed: that's the last message while the turn is active. So we
            gate on isFinalReply and hide only when (isLast && turnActive), not on
            turnActive globally. (Not keyed off isLast: a trailing `result` —
            "Done — 27.5s" — is the last array item, not the reply.) */}
        {isFinalReply && !showCursor && !(isLast && turnActive) && copyable && (
          <div className="flex items-center gap-0.5 -ml-1">
            <CopyMessageButton text={copyable} />
            {speakable && <ListenButton id={speechId} text={speakable} />}
          </div>
        )}
      </div>
    )
  }

  if (message.type === 'result') {
    const data = message.data
    // Show result text if present (slash command output, error messages)
    if (data.result) {
      return (
        <div>
          <div className="prose dark:prose-invert max-w-none break-words prose-pre:bg-muted prose-pre:border prose-pre:border-border prose-code:text-foreground prose-code:before:content-none prose-code:after:content-none prose-p:my-1.5 prose-headings:my-2 prose-a:break-words">
            <MarkdownView text={data.result} />
          </div>
        </div>
      )
    }
    return (
      <div className="text-xs text-muted-foreground/60 px-1">
        {data.is_error ? 'Error' : 'Done'} — {(data.duration_ms / 1000).toFixed(1)}s
      </div>
    )
  }

  if (message.type === 'tool_use_summary') {
    return (
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground/70 px-1">
        <Zap className="w-3 h-3" />
        <span>{message.summary}</span>
      </div>
    )
  }

  if (message.type === 'screenshot_result') {
    return <ScreenshotPreview filename={message.filename} mediaType={message.media_type} data={message.data} />
  }

  return null
})

// ─── Chat Images: full-width previews + lightbox ────────────────────────────

const LIGHTBOX_MAX_SCALE = 6
const LIGHTBOX_DOUBLE_TAP_SCALE = 2.5
const DOUBLE_TAP_MS = 300

/**
 * Full-screen image viewer with its own zoom: the app's viewport meta turns
 * page zoom off (`user-scalable=no`), so pinch/pan/wheel are implemented on a
 * transformed wrapper via Pointer Events (one code path for touch + mouse).
 * Gestures: pinch to zoom (two pointers), drag to pan while zoomed, wheel /
 * trackpad-pinch to zoom on desktop, double-tap (or double-click) to toggle
 * zoom at that spot. A single tap at 1x closes — slightly deferred so it can
 * be cancelled if it turns out to be the first half of a double-tap.
 */
function ImageLightbox({ src, alt, onClose }: { src: string; alt: string; onClose: () => void }) {
  const containerRef = useRef<HTMLDivElement>(null)
  const zoomRef = useRef<HTMLDivElement>(null)
  // Transform state lives in refs and is applied straight to the style —
  // per-frame React re-renders during a pinch would just add latency.
  const stateRef = useRef({ scale: 1, tx: 0, ty: 0 })
  const pointersRef = useRef(new Map<number, { x: number; y: number }>())
  const pinchRef = useRef<{ dist: number; cx: number; cy: number } | null>(null)
  const downRef = useRef<{ t: number; x: number; y: number } | null>(null)
  const lastTapRef = useRef<{ t: number; x: number; y: number } | null>(null)
  const closeTimerRef = useRef<number | null>(null)

  const cancelPendingClose = () => {
    if (closeTimerRef.current !== null) {
      window.clearTimeout(closeTimerRef.current)
      closeTimerRef.current = null
    }
  }

  const apply = () => {
    const s = stateRef.current
    if (zoomRef.current) zoomRef.current.style.transform = `translate(${s.tx}px, ${s.ty}px) scale(${s.scale})`
  }

  const clampAndApply = () => {
    const s = stateRef.current
    const c = containerRef.current
    if (s.scale <= 1.02) {
      s.scale = 1
      s.tx = 0
      s.ty = 0
    } else if (c) {
      // Loose bound: with object-contain the image box equals the container
      // box, so this keeps content from being flung fully off-screen.
      const maxX = ((s.scale - 1) * c.clientWidth) / 2
      const maxY = ((s.scale - 1) * c.clientHeight) / 2
      s.tx = Math.max(-maxX, Math.min(maxX, s.tx))
      s.ty = Math.max(-maxY, Math.min(maxY, s.ty))
    }
    apply()
  }

  /** Zoom by `factor` keeping the viewport point (clientX/Y) visually fixed. */
  const zoomAt = (clientX: number, clientY: number, factor: number) => {
    const c = containerRef.current
    if (!c) return
    const rect = c.getBoundingClientRect()
    const px = clientX - rect.left - rect.width / 2
    const py = clientY - rect.top - rect.height / 2
    const s = stateRef.current
    const newScale = Math.max(1, Math.min(LIGHTBOX_MAX_SCALE, s.scale * factor))
    const r = newScale / s.scale
    s.tx = px - r * (px - s.tx)
    s.ty = py - r * (py - s.ty)
    s.scale = newScale
    clampAndApply()
  }

  const onPointerDown = (e: React.PointerEvent) => {
    const el = containerRef.current
    el?.setPointerCapture(e.pointerId)
    pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY })
    if (pointersRef.current.size === 1) {
      downRef.current = { t: performance.now(), x: e.clientX, y: e.clientY }
    } else {
      // A second finger means a pinch, not a tap — nothing should close.
      cancelPendingClose()
      downRef.current = null
      lastTapRef.current = null
      pinchRef.current = null
    }
  }

  const onPointerMove = (e: React.PointerEvent) => {
    const pointers = pointersRef.current
    if (!pointers.has(e.pointerId)) return
    const prev = pointers.get(e.pointerId)!
    pointers.set(e.pointerId, { x: e.clientX, y: e.clientY })

    if (pointers.size === 2) {
      const [a, b] = Array.from(pointers.values())
      const dist = Math.hypot(a.x - b.x, a.y - b.y)
      const cx = (a.x + b.x) / 2
      const cy = (a.y + b.y) / 2
      const pinch = pinchRef.current
      if (pinch && pinch.dist > 0) {
        // Pan with the midpoint, then scale around it.
        stateRef.current.tx += cx - pinch.cx
        stateRef.current.ty += cy - pinch.cy
        zoomAt(cx, cy, dist / pinch.dist)
      }
      pinchRef.current = { dist, cx, cy }
    } else if (pointers.size === 1 && stateRef.current.scale > 1) {
      stateRef.current.tx += e.clientX - prev.x
      stateRef.current.ty += e.clientY - prev.y
      clampAndApply()
    }
  }

  const onPointerEnd = (e: React.PointerEvent) => {
    const pointers = pointersRef.current
    pointers.delete(e.pointerId)
    if (pointers.size < 2) pinchRef.current = null

    const down = downRef.current
    downRef.current = null
    if (!down || pointers.size !== 0 || e.type === 'pointercancel') return
    const moved = Math.hypot(e.clientX - down.x, e.clientY - down.y)
    const quick = performance.now() - down.t < 350
    if (!quick || moved > 12) return

    // A clean tap. Double-tap toggles zoom; a lone tap at 1x closes (deferred
    // past the double-tap window); a lone tap while zoomed does nothing, so a
    // stray touch never dumps the user out mid-inspection.
    const now = performance.now()
    const last = lastTapRef.current
    if (last && now - last.t < DOUBLE_TAP_MS && Math.hypot(e.clientX - last.x, e.clientY - last.y) < 40) {
      cancelPendingClose()
      lastTapRef.current = null
      const s = stateRef.current
      if (s.scale > 1) {
        s.scale = 1
        clampAndApply()
      } else {
        zoomAt(e.clientX, e.clientY, LIGHTBOX_DOUBLE_TAP_SCALE)
      }
      return
    }
    lastTapRef.current = { t: now, x: e.clientX, y: e.clientY }
    if (stateRef.current.scale === 1) {
      cancelPendingClose()
      closeTimerRef.current = window.setTimeout(onClose, DOUBLE_TAP_MS)
    }
  }

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    // Wheel must be non-passive to preventDefault (stops page/pinch zoom of
    // the app shell); React's synthetic onWheel can't guarantee that.
    const el = containerRef.current
    const onWheel = (e: WheelEvent) => {
      e.preventDefault()
      zoomAt(e.clientX, e.clientY, Math.exp(-e.deltaY * 0.0022))
    }
    el?.addEventListener('wheel', onWheel, { passive: false })
    return () => {
      window.removeEventListener('keydown', onKey)
      el?.removeEventListener('wheel', onWheel)
      cancelPendingClose()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onClose])

  return createPortal(
    <div className="fixed inset-0 z-[100] bg-black/90 overscroll-contain">
      <div
        ref={containerRef}
        className="absolute inset-0 flex items-center justify-center p-3"
        style={{ touchAction: 'none' }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerEnd}
        onPointerCancel={onPointerEnd}
      >
        <div ref={zoomRef} className="w-full h-full flex items-center justify-center will-change-transform">
          <img
            src={src}
            alt={alt}
            draggable={false}
            className="max-w-full max-h-full object-contain select-none pointer-events-none"
            style={{ WebkitTouchCallout: 'none' } as React.CSSProperties}
          />
        </div>
      </div>
      <button
        type="button"
        aria-label="Close image"
        onClick={onClose}
        className="absolute top-3 right-3 z-10 p-2 rounded-full bg-black/50 text-white/80 hover:text-white transition-colors"
      >
        <X className="w-5 h-5" />
      </button>
    </div>,
    document.body,
  )
}

/** In-chat image: full bubble width up to a sane height, tap to open the
 *  lightbox. Used for user attachments and screenshot results alike. */
function ChatImage({ src, alt }: { src: string; alt: string }) {
  const [open, setOpen] = useState(false)
  return (
    <>
      <img
        src={src}
        alt={alt}
        onClick={() => setOpen(true)}
        className="w-full max-h-[45vh] rounded-lg border border-border object-contain bg-secondary/30 cursor-zoom-in"
      />
      {open && <ImageLightbox src={src} alt={alt} onClose={() => setOpen(false)} />}
    </>
  )
}

// ─── Screenshot Preview ─────────────────────────────────────────────────────

function ScreenshotPreview({ filename, mediaType, data }: { filename: string; mediaType: string; data: string }) {
  // Handle stripped screenshots (data removed during persistence)
  if (!data) {
    return (
      <div className="text-xs text-muted-foreground flex items-center gap-1">
        <FileText className="w-3 h-3" />
        <span>{filename}</span>
        <span className="italic">(screenshot not available)</span>
      </div>
    )
  }

  const src = `data:${mediaType};base64,${data}`

  return (
    <div className="space-y-1">
      <div className="text-xs text-muted-foreground flex items-center gap-1">
        <FileText className="w-3 h-3" />
        <span>{filename}</span>
      </div>
      <ChatImage src={src} alt={filename} />
    </div>
  )
}

// ─── Tool Grouping ──────────────────────────────────────────────────────────

type ContentGroup = {
  type: 'single'
  block: ContentBlock
} | {
  type: 'tool_group'
  toolName: string
  items: { use: ContentBlock; result?: ContentBlock }[]
}

/** Group consecutive tool_use + tool_result pairs of the same tool name */
function groupContentBlocks(blocks: ContentBlock[]): ContentGroup[] {
  const groups: ContentGroup[] = []
  let i = 0

  while (i < blocks.length) {
    const block = blocks[i]

    // Skip hidden tool calls and their results entirely
    if (block.type === 'tool_use' && block.name && HIDDEN_TOOLS.has(block.name)) {
      const useId = block.id
      i++
      // Also skip the matching tool_result if present
      if (i < blocks.length && blocks[i].type === 'tool_result' && blocks[i].tool_use_id === useId) {
        i++
      }
      continue
    }

    if (block.type === 'tool_use' && block.name) {
      // Collect consecutive tool_use blocks of the same tool name.
      // For each, scan ahead for a matching tool_result (may not be immediately adjacent).
      const toolName = block.name
      const items: { use: ContentBlock; result?: ContentBlock }[] = []

      while (i < blocks.length && blocks[i].type === 'tool_use' && blocks[i].name === toolName) {
        const use = blocks[i]
        i++
        // Check if next block is a tool_result matching this tool_use
        let result: ContentBlock | undefined
        if (i < blocks.length && blocks[i].type === 'tool_result' && blocks[i].tool_use_id === use.id) {
          result = blocks[i]
          i++
        } else {
          // Scan ahead for a matching tool_result (may be separated by other blocks)
          for (let j = i; j < blocks.length; j++) {
            if (blocks[j].type === 'tool_result' && blocks[j].tool_use_id === use.id) {
              result = blocks[j]
              break
            }
          }
        }
        items.push({ use, result })
      }

      groups.push({ type: 'tool_group', toolName, items })
    } else {
      groups.push({ type: 'single', block })
      i++
    }
  }

  return groups
}

function ContentGroupView({ group }: { group: ContentGroup }) {
  if (group.type === 'single') {
    return <ContentBlockView block={group.block} />
  }

  // Tool group — single tool rendered inline, multiple get a count badge
  if (group.items.length === 1) {
    const { use, result } = group.items[0]
    return (
      <>
        <ToolUseView block={use} />
        {result && <ToolResultView block={result} toolName={use.name} />}
      </>
    )
  }

  return <CollapsedToolGroup toolName={group.toolName} items={group.items} />
}

function CollapsedToolGroup({ toolName, items }: { toolName: string; items: { use: ContentBlock; result?: ContentBlock }[] }) {
  const preview = items.map(({ use }) => formatToolSummary(toolName, use.input)).filter(Boolean).join(', ')

  return (
    <details className="group rounded border border-border/50 bg-secondary/20 text-xs">
      <summary className="flex items-center gap-1.5 px-2 py-1.5 cursor-pointer select-none hover:bg-secondary/40 transition-colors">
        <ChevronRight className="w-3 h-3 text-muted-foreground transition-transform group-open:rotate-90" />
        <span className="font-medium text-primary">{toolName}</span>
        <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-muted text-muted-foreground text-[10px] font-semibold">
          {items.length}
        </span>
        {preview && <span className="text-muted-foreground truncate ml-1">{preview}</span>}
      </summary>
      <div className="space-y-1.5 px-2 pb-2 pt-1.5 border-t border-border/30">
        {items.map(({ use, result }, j) => (
          <div key={j} className="space-y-1">
            <ToolUseView block={use} />
            {result && <ToolResultView block={result} toolName={use.name} />}
          </div>
        ))}
      </div>
    </details>
  )
}

// ─── Content Block Views ────────────────────────────────────────────────────

function ContentBlockView({ block }: { block: ContentBlock }) {
  if (block.type === 'text' && block.text) {
    return (
      <div className="prose dark:prose-invert max-w-none break-words prose-pre:bg-muted prose-pre:border prose-pre:border-border prose-code:text-foreground prose-code:before:content-none prose-code:after:content-none prose-p:my-1.5 prose-headings:my-2 prose-ul:my-1.5 prose-ol:my-1.5 prose-li:my-0.5 prose-a:break-words">
        <MarkdownView text={block.text} />
      </div>
    )
  }

  if (block.type === 'tool_use') {
    return <ToolUseView block={block} />
  }

  if (block.type === 'tool_result') {
    return <ToolResultView block={block} />
  }

  if (block.type === 'thinking' && block.thinking) {
    const len = block.thinking.length
    const sizeLabel = len >= 1000 ? `${(len / 1000).toFixed(1)}k` : String(len)
    const preview = block.thinking.replace(/\s+/g, ' ').substring(0, 100)
    return (
      <details className="group text-xs text-muted-foreground">
        <summary className="flex items-center gap-1.5 cursor-pointer hover:text-foreground select-none">
          <ChevronRight className="w-3 h-3 transition-transform group-open:rotate-90" />
          <span>Thinking</span>
          <span className="opacity-50">({sizeLabel} chars)</span>
          <span className="hidden group-[&:not([open])]:inline truncate opacity-40 ml-1">{preview}</span>
        </summary>
        <pre className="mt-1 whitespace-pre-wrap">{block.thinking}</pre>
      </details>
    )
  }

  return null
}

// Tools that are handled via interactive UI (QuestionCard) or have no user-visible output
const HIDDEN_TOOLS = new Set(['EnterPlanMode', 'ExitPlanMode', 'AskUserQuestion'])

function ToolUseView({ block }: { block: ContentBlock }) {
  // Hide plan mode tools — they're handled via QuestionCard or have no user-visible output
  if (block.name && HIDDEN_TOOLS.has(block.name)) return null

  const summary = formatToolSummary(block.name || '', block.input)
  const desc = typeof block.input?.description === 'string' ? block.input.description : ''
  const name = block.name || ''

  // Render tool-specific detail inside the collapsible
  const inp = block.input || {}
  const str = (key: string) => typeof inp[key] === 'string' ? inp[key] as string : ''
  const longDesc = desc && desc.length > 80 ? <div className="text-muted-foreground italic">{desc}</div> : null

  const renderDetail = () => {
    if (!block.input) return null

    switch (name) {
      case 'Edit': {
        const oldStr = str('old_string'), newStr = str('new_string')
        if (!oldStr && !newStr) break
        return (
          <div className="space-y-1.5">
            {longDesc}
            {!!inp.replace_all && (
              <span className="inline-block text-[10px] font-medium px-1.5 py-0.5 rounded bg-yellow-500/10 text-yellow-600 dark:text-yellow-400">replace all</span>
            )}
            <InlineDiff oldText={oldStr} newText={newStr} fileName={str('file_path')} />
          </div>
        )
      }
      case 'Write': {
        const writeContent = str('content')
        if (!writeContent) break
        const writePath = str('file_path')
        // Plan files — render as readable Markdown instead of a diff
        if (writePath.includes('.claude/plans/')) {
          return (
            <div className="space-y-1.5">
              <div className="text-muted-foreground/60 font-mono text-[11px]">{writePath.split('/').pop()}</div>
              <div className="prose dark:prose-invert max-w-none break-words prose-pre:bg-muted prose-pre:border prose-pre:border-border prose-code:text-foreground prose-code:before:content-none prose-code:after:content-none prose-p:my-1.5 prose-headings:my-2 prose-ul:my-1.5 prose-ol:my-1.5 prose-li:my-0.5 prose-a:break-words text-sm">
                <MarkdownView text={writeContent} />
              </div>
            </div>
          )
        }
        return (
          <div className="space-y-1.5">
            {longDesc}
            <InlineDiff newText={writeContent} fileName={writePath} />
          </div>
        )
      }
      case 'Bash':
        if (!str('command')) break
        return (
          <div className="space-y-1.5">
            {longDesc}
            <pre className="text-muted-foreground overflow-x-auto whitespace-pre-wrap font-mono bg-muted rounded px-2 py-1.5">
              <span className="text-muted-foreground/50 select-none">$ </span>{str('command')}
            </pre>
            {inp.timeout != null && <div className="text-muted-foreground/50">timeout: {String(inp.timeout)}ms</div>}
          </div>
        )
      case 'Read':
        return (
          <div className="space-y-1.5">
            {longDesc}
            <code className="font-mono text-foreground/80">{str('file_path') || str('path')}</code>
            {(inp.offset != null || inp.limit != null) && (
              <div className="flex gap-3 text-muted-foreground">
                {inp.offset != null && <span>offset: <span className="text-foreground/70">{String(inp.offset)}</span></span>}
                {inp.limit != null && <span>limit: <span className="text-foreground/70">{String(inp.limit)}</span></span>}
              </div>
            )}
          </div>
        )
      case 'Glob':
        return (
          <div className="space-y-1.5">
            {longDesc}
            <code className="font-mono bg-muted rounded px-1.5 py-0.5 text-foreground/80">{str('pattern')}</code>
            {str('path') && <div className="text-muted-foreground">in: <span className="font-mono text-foreground/70">{str('path')}</span></div>}
          </div>
        )
      case 'Grep':
        return (
          <div className="space-y-1.5">
            {longDesc}
            <pre className="font-mono bg-muted rounded px-2 py-1 text-foreground/80 overflow-x-auto whitespace-pre-wrap">{str('pattern')}</pre>
            <div className="flex flex-wrap gap-x-3 gap-y-1 text-muted-foreground">
              {str('path') && <span>path: <span className="font-mono text-foreground/70">{str('path')}</span></span>}
              {str('glob') && <span>glob: <span className="font-mono text-foreground/70">{str('glob')}</span></span>}
              {str('output_mode') && <span>mode: <span className="text-foreground/70">{str('output_mode')}</span></span>}
              {inp.context != null && <span>context: <span className="text-foreground/70">{String(inp.context)}</span></span>}
              {inp.head_limit != null && <span>limit: <span className="text-foreground/70">{String(inp.head_limit)}</span></span>}
            </div>
          </div>
        )
      case 'Agent':
        return (
          <div className="space-y-1.5">
            {str('description') && <div className="font-medium text-foreground/80">{str('description')}</div>}
            {str('subagent_type') && (
              <span className="inline-block text-[10px] font-medium px-1.5 py-0.5 rounded bg-primary/10 text-primary">{str('subagent_type')}</span>
            )}
            {str('prompt') && (
              <pre className="font-mono bg-muted rounded px-2 py-1.5 text-muted-foreground overflow-x-auto whitespace-pre-wrap max-h-40 overflow-y-auto">
                {str('prompt').substring(0, 2000)}
              </pre>
            )}
          </div>
        )
      case 'ToolSearch':
        return (
          <div className="space-y-1">
            {longDesc}
            <code className="font-mono text-foreground/80">{str('query')}</code>
          </div>
        )
      case 'WebSearch':
        return (
          <div className="space-y-1">
            {longDesc}
            <div className="font-medium text-foreground/80">{str('query')}</div>
            {Array.isArray(inp.allowed_domains) && (inp.allowed_domains as string[]).length > 0 && (
              <div className="text-muted-foreground">domains: {(inp.allowed_domains as string[]).join(', ')}</div>
            )}
          </div>
        )
      case 'WebFetch':
        return (
          <div className="space-y-1">
            {longDesc}
            {str('url') && <code className="font-mono text-primary break-all">{str('url')}</code>}
            {str('prompt') && <div className="text-muted-foreground italic line-clamp-2">{str('prompt')}</div>}
          </div>
        )
      case 'TodoWrite': {
        const todos = Array.isArray(inp.todos) ? inp.todos as { content?: string; status?: string }[] : null
        if (!todos) break
        return (
          <div className="space-y-1">
            {todos.map((todo, i) => (
              <div key={i} className="flex items-start gap-1.5">
                <span className="shrink-0 mt-0.5">
                  {todo.status === 'completed' ? '✓' : todo.status === 'in_progress' ? '◐' : '○'}
                </span>
                <span className={todo.status === 'completed' ? 'line-through text-muted-foreground' : 'text-foreground/80'}>
                  {todo.content || JSON.stringify(todo)}
                </span>
              </div>
            ))}
          </div>
        )
      }
      case 'NotebookEdit':
        return (
          <div className="space-y-1.5">
            {str('notebook_path') && <code className="font-mono text-muted-foreground">{str('notebook_path')}</code>}
            <div className="flex gap-3 text-muted-foreground">
              {str('cell_type') && <span>type: <span className="text-foreground/70">{str('cell_type')}</span></span>}
              {str('edit_mode') && <span>mode: <span className="text-foreground/70">{str('edit_mode')}</span></span>}
              {inp.cell_number != null && <span>cell: <span className="text-foreground/70">{String(inp.cell_number)}</span></span>}
            </div>
            {str('new_source') && (
              <pre className="font-mono bg-muted rounded px-2 py-1.5 text-muted-foreground overflow-x-auto whitespace-pre-wrap max-h-40 overflow-y-auto">
                {str('new_source').substring(0, 2000)}
              </pre>
            )}
          </div>
        )
      case 'Skill':
        return (
          <div className="space-y-1">
            {longDesc}
            {str('skill') && <div className="font-medium text-foreground/80">/{str('skill')}</div>}
            {str('args') && <div className="text-muted-foreground font-mono">{str('args')}</div>}
          </div>
        )
      case 'CronCreate':
        return (
          <div className="space-y-1.5">
            {longDesc}
            {str('cron') && (
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground">schedule:</span>
                <code className="font-mono bg-muted rounded px-1.5 py-0.5 text-foreground/80">{str('cron')}</code>
              </div>
            )}
            {str('prompt') && <div className="text-foreground/80">{str('prompt')}</div>}
            {inp.recurring != null && (
              <span className={`inline-block text-[10px] font-medium px-1.5 py-0.5 rounded ${inp.recurring ? 'bg-green-500/10 text-green-600 dark:text-green-400' : 'bg-muted text-muted-foreground'}`}>
                {inp.recurring ? 'recurring' : 'one-shot'}
              </span>
            )}
          </div>
        )
      case 'CronDelete':
        return (
          <div className="space-y-1">
            {longDesc}
            {str('id') && <div className="text-muted-foreground">id: <code className="font-mono text-foreground/80">{str('id')}</code></div>}
          </div>
        )
      case 'CronList':
        return <div className="text-muted-foreground">{longDesc || 'List scheduled tasks'}</div>
      case 'TaskCreate':
      case 'TaskUpdate':
        return (
          <div className="space-y-1">
            {longDesc}
            {str('description') && <div className="text-foreground/80">{str('description')}</div>}
            {str('status') && (
              <span className={`inline-block text-[10px] font-medium px-1.5 py-0.5 rounded ${
                str('status') === 'completed' ? 'bg-green-500/10 text-green-600 dark:text-green-400'
                  : str('status') === 'in_progress' ? 'bg-blue-500/10 text-blue-600 dark:text-blue-400'
                  : str('status') === 'blocked' ? 'bg-red-500/10 text-red-600 dark:text-red-400'
                  : 'bg-muted text-muted-foreground'
              }`}>
                {str('status')}
              </span>
            )}
            {str('prompt') && (
              <pre className="font-mono bg-muted rounded px-2 py-1.5 text-muted-foreground overflow-x-auto whitespace-pre-wrap max-h-32 overflow-y-auto scrollbar-thin">
                {str('prompt').substring(0, 1000)}
              </pre>
            )}
          </div>
        )
      case 'TaskGet':
      case 'TaskList':
      case 'TaskStop':
        return (
          <div className="space-y-1">
            {longDesc}
            {str('id') && <div className="text-muted-foreground">id: <code className="font-mono text-foreground/80">{str('id')}</code></div>}
          </div>
        )
      case 'TaskOutput':
        return (
          <div className="space-y-1">
            {longDesc}
            {str('id') && <div className="text-muted-foreground">id: <code className="font-mono text-foreground/80">{str('id')}</code></div>}
            {inp.offset != null && <div className="text-muted-foreground">offset: <span className="text-foreground/70">{String(inp.offset)}</span></div>}
          </div>
        )
      case 'LSP':
        return (
          <div className="space-y-1">
            {longDesc}
            {str('command') && <code className="font-mono text-foreground/80">{str('command')}</code>}
            {str('file_path') && <div className="text-muted-foreground font-mono">{str('file_path')}</div>}
          </div>
        )
      case 'EnterWorktree':
        return (
          <div className="space-y-1">
            {longDesc}
            {str('branch') && <div className="text-muted-foreground">branch: <code className="font-mono text-foreground/80">{str('branch')}</code></div>}
          </div>
        )
      case 'SendMessage':
        return (
          <div className="space-y-1">
            {str('recipient') && <div className="text-muted-foreground">to: <span className="font-medium text-foreground/80">{str('recipient')}</span></div>}
            {str('content') && <div className="text-foreground/80 whitespace-pre-wrap">{str('content').substring(0, 500)}</div>}
          </div>
        )
    }

    // Fallback: raw JSON for unknown tools
    return (
      <div className="space-y-1.5">
        {longDesc}
        <pre className="text-muted-foreground overflow-x-auto whitespace-pre-wrap">
          {JSON.stringify(block.input, null, 2).substring(0, 1000)}
        </pre>
      </div>
    )
  }

  // Auto-expand plan file writes so the plan content is visible
  const autoExpand = name === 'Write' && typeof inp.file_path === 'string' && (inp.file_path as string).includes('.claude/plans/')

  return (
    <details className="group rounded border border-border/50 bg-secondary/20 text-xs" open={autoExpand || undefined}>
      <summary className="flex items-center gap-1.5 px-2 py-1.5 cursor-pointer select-none hover:bg-secondary/40 transition-colors">
        <ChevronRight className="w-3 h-3 text-muted-foreground transition-transform group-open:rotate-90" />
        <span className="font-medium text-primary">{name}</span>
        {summary && <span className="text-muted-foreground truncate">{summary}</span>}
      </summary>
      <div className="border-t border-border/30 pt-1.5 mx-2 mb-1">
        {renderDetail()}
      </div>
    </details>
  )
}

// ─── Inline Diff Viewer ──────────────────────────────────────────────────────

interface DiffLine {
  type: 'add' | 'del' | 'context'
  content: string
  wordChanges?: { value: string; added?: boolean; removed?: boolean }[]
}

/** Simple Myers-like diff on lines, returning edit script */
function diffLines(oldLines: string[], newLines: string[]): DiffLine[] {
  // Build LCS table
  const m = oldLines.length, n = newLines.length
  // For very large files, skip LCS and just show all del + all add
  if (m + n > 2000) {
    return [
      ...oldLines.map(l => ({ type: 'del' as const, content: l })),
      ...newLines.map(l => ({ type: 'add' as const, content: l })),
    ]
  }
  const dp: number[][] = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0))
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i][j] = oldLines[i - 1] === newLines[j - 1]
        ? dp[i - 1][j - 1] + 1
        : Math.max(dp[i - 1][j], dp[i][j - 1])
    }
  }
  // Backtrack to produce diff
  const result: DiffLine[] = []
  let i = m, j = n
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && oldLines[i - 1] === newLines[j - 1]) {
      result.push({ type: 'context', content: oldLines[i - 1] })
      i--; j--
    } else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
      result.push({ type: 'add', content: newLines[j - 1] })
      j--
    } else {
      result.push({ type: 'del', content: oldLines[i - 1] })
      i--
    }
  }
  result.reverse()
  return result
}

/** Word-level diff between two strings */
function diffWords(oldStr: string, newStr: string): { value: string; added?: boolean; removed?: boolean }[] {
  const splitWords = (s: string) => s.match(/\S+|\s+/g) || []
  const oldW = splitWords(oldStr), newW = splitWords(newStr)
  if (oldW.length + newW.length > 500) return [{ value: oldStr, removed: true }, { value: newStr, added: true }]

  const m = oldW.length, n = newW.length
  const dp: number[][] = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0))
  for (let i = 1; i <= m; i++)
    for (let j = 1; j <= n; j++)
      dp[i][j] = oldW[i - 1] === newW[j - 1] ? dp[i - 1][j - 1] + 1 : Math.max(dp[i - 1][j], dp[i][j - 1])

  const parts: { value: string; added?: boolean; removed?: boolean }[] = []
  let ii = m, jj = n
  while (ii > 0 || jj > 0) {
    if (ii > 0 && jj > 0 && oldW[ii - 1] === newW[jj - 1]) {
      parts.push({ value: oldW[ii - 1] })
      ii--; jj--
    } else if (jj > 0 && (ii === 0 || dp[ii][jj - 1] >= dp[ii - 1][jj])) {
      parts.push({ value: newW[jj - 1], added: true })
      jj--
    } else {
      parts.push({ value: oldW[ii - 1], removed: true })
      ii--
    }
  }
  parts.reverse()
  // Merge consecutive same-type parts
  const merged: typeof parts = []
  for (const p of parts) {
    const last = merged[merged.length - 1]
    if (last && last.added === p.added && last.removed === p.removed) {
      last.value += p.value
    } else {
      merged.push({ ...p })
    }
  }
  return merged
}

function computeDiffLines(oldText: string, newText: string): DiffLine[] {
  const oldLines = oldText.split('\n')
  const newLines = newText.split('\n')
  const raw = diffLines(oldLines, newLines)

  // Filter to show only changed lines + 3 lines of context
  const changed = new Set<number>()
  raw.forEach((line, idx) => { if (line.type !== 'context') changed.add(idx) })
  const visible = new Set<number>()
  for (const idx of changed) {
    for (let k = Math.max(0, idx - 3); k <= Math.min(raw.length - 1, idx + 3); k++) {
      visible.add(k)
    }
  }
  const lines = raw.filter((_, idx) => visible.has(idx))

  // Add word-level highlighting for adjacent del/add pairs
  let i = 0
  while (i < lines.length) {
    const delStart = i
    while (i < lines.length && lines[i].type === 'del') i++
    const delEnd = i
    const addStart = i
    while (i < lines.length && lines[i].type === 'add') i++
    const addEnd = i

    const delCount = delEnd - delStart
    const addCount = addEnd - addStart
    if (delCount > 0 && addCount > 0) {
      const pairs = Math.min(delCount, addCount)
      for (let j = 0; j < pairs; j++) {
        const wd = diffWords(lines[delStart + j].content, lines[addStart + j].content)
        lines[delStart + j].wordChanges = wd.filter(p => !p.added)
        lines[addStart + j].wordChanges = wd.filter(p => !p.removed)
      }
    }
    if (i === delStart) i++
  }

  return lines
}

function InlineDiff({
  oldText,
  newText,
  fileName,
}: {
  oldText?: string
  newText: string
  fileName?: string
}) {
  const lines: DiffLine[] = useMemo(() => {
    if (oldText != null && oldText !== '') {
      return computeDiffLines(oldText, newText)
    }
    // Write (new file) — all lines are additions
    return newText.split('\n').map(line => ({ type: 'add' as const, content: line }))
  }, [oldText, newText])

  // Truncate long diffs
  const MAX_LINES = 60
  const truncated = lines.length > MAX_LINES
  const visibleLines = truncated ? lines.slice(0, MAX_LINES) : lines

  const shortName = fileName ? (fileName.split('/').pop() || fileName) : ''
  const dirPath = fileName && shortName !== fileName ? fileName.slice(0, fileName.length - shortName.length) : ''

  return (
    <div className="rounded-lg border border-border overflow-hidden bg-muted max-h-[280px] overflow-y-auto font-mono text-[12px] leading-[1.5] scrollbar-thin">
      {fileName && (
        <div className="flex items-center gap-1.5 px-3 py-1 bg-secondary/30 border-b border-border text-[11px] sticky top-0 z-[1]">
          <FileText className="w-3 h-3 text-muted-foreground shrink-0" />
          {dirPath && <span className="text-muted-foreground">{dirPath}</span>}
          <span className="font-semibold text-foreground">{shortName}</span>
        </div>
      )}
      <div>
        {visibleLines.map((line, i) => (
          <div
            key={i}
            className={
              line.type === 'add'
                ? 'bg-green-500/10 dark:bg-green-500/8'
                : line.type === 'del'
                  ? 'bg-red-500/10 dark:bg-red-500/8'
                  : ''
            }
            style={{ display: 'flex', minHeight: 20 }}
          >
            <span
              className={`shrink-0 w-4 text-center select-none ${
                line.type === 'add'
                  ? 'text-green-600 dark:text-green-400'
                  : line.type === 'del'
                    ? 'text-red-600 dark:text-red-400'
                    : 'text-muted-foreground/30'
              }`}
            >
              {line.type === 'add' ? '+' : line.type === 'del' ? '-' : ' '}
            </span>
            <span className="flex-1 min-w-0 whitespace-pre-wrap break-all pr-3 text-foreground/80">
              {line.wordChanges ? (
                line.wordChanges.map((part, j) => {
                  if (part.added) return <span key={j} className="bg-green-500/25 dark:bg-green-400/20 rounded-sm px-px">{part.value}</span>
                  if (part.removed) return <span key={j} className="bg-red-500/25 dark:bg-red-400/20 rounded-sm px-px">{part.value}</span>
                  return <span key={j}>{part.value}</span>
                })
              ) : (
                line.content || '\u00A0'
              )}
            </span>
          </div>
        ))}
        {truncated && (
          <div className="px-3 py-1 text-muted-foreground text-[11px] border-t border-border/30">
            ... {lines.length - MAX_LINES} more lines
          </div>
        )}
      </div>
    </div>
  )
}

/** Flatten tool_result content into displayable text. MCP tools return block
 * arrays ([{type:'text',text}, {type:'image',...}]) — extract the text rather
 * than dumping raw JSON at the user. */
function toolResultText(content: ContentBlock['content']): string {
  if (typeof content === 'string') return content
  if (!Array.isArray(content)) return content == null ? '' : JSON.stringify(content)
  return content
    .map((b) => {
      if (b && b.type === 'text' && typeof b.text === 'string') return b.text
      if (b && b.type === 'image') return '[image]'
      return JSON.stringify(b)
    })
    .join('\n')
}

function ToolResultView({ block, toolName }: { block: ContentBlock; toolName?: string }) {
  const content = toolResultText(block.content)
  const label = block.is_error ? 'Error' : 'Result'
  const [expanded, setExpanded] = useState(false)

  const allLines = (content || '').split('\n')
  const totalLines = allLines.length
  const TAIL_LINES = 20
  // Don't abbreviate Edit/Write results — the diff in ToolUseView is the primary display
  const skipAbbreviation = toolName === 'Edit' || toolName === 'Write'
  const isTruncatable = !skipAbbreviation && totalLines > TAIL_LINES
  const displayText = isTruncatable && !expanded
    ? allLines.slice(-TAIL_LINES).join('\n')
    : content || ''

  return (
    <details className="group rounded border border-border/50 bg-secondary/10 text-xs">
      <summary className={`flex items-center gap-1.5 px-2 py-1.5 cursor-pointer select-none hover:bg-secondary/30 transition-colors ${block.is_error ? 'text-destructive' : 'text-muted-foreground'}`}>
        <ChevronRight className="w-3 h-3 transition-transform group-open:rotate-90" />
        <span>{label}</span>
        {isTruncatable && <span className="opacity-50">{totalLines} lines</span>}
        <span className="truncate opacity-70">{(content || '').substring(0, 80)}</span>
      </summary>
      <div className="border-t border-border/30 pt-1.5 mx-2 mb-1">
        {isTruncatable && !expanded && (
          <button
            onClick={(e) => { e.stopPropagation(); setExpanded(true) }}
            className="text-primary hover:underline mb-1"
          >
            Show all {totalLines} lines
          </button>
        )}
        {isTruncatable && expanded && (
          <button
            onClick={(e) => { e.stopPropagation(); setExpanded(false) }}
            className="text-primary hover:underline mb-1"
          >
            Show last {TAIL_LINES} lines
          </button>
        )}
        <pre className="text-muted-foreground overflow-x-auto whitespace-pre-wrap">
          {displayText}
        </pre>
      </div>
    </details>
  )
}

/** Generate a short inline summary for a tool call — prefers description when short enough */
function formatToolSummary(name: string, input?: Record<string, unknown>): string {
  if (!input) return ''
  // Prefer input.description if available and short enough (like reference app)
  const desc = typeof input.description === 'string' ? input.description : ''
  if (desc && desc.length <= 80) return desc
  switch (name) {
    case 'Bash':
      return typeof input.command === 'string' ? input.command.substring(0, 60) : desc
    case 'Read':
      return typeof input.file_path === 'string' ? input.file_path.split('/').pop() || '' : desc
    case 'Edit':
    case 'Write':
      return typeof input.file_path === 'string' ? input.file_path.split('/').pop() || '' : desc
    case 'Grep':
      return typeof input.pattern === 'string' ? `/${input.pattern}/` : desc
    case 'Glob':
      return typeof input.pattern === 'string' ? input.pattern : desc
    case 'Agent':
      return desc
    case 'ToolSearch':
      return typeof input.query === 'string' ? input.query : desc
    case 'TodoWrite':
      return desc
    case 'CronCreate':
      return typeof input.cron === 'string' ? input.cron : desc
    case 'CronDelete':
    case 'TaskGet':
    case 'TaskStop':
    case 'TaskOutput':
      return typeof input.id === 'string' ? input.id : desc
    case 'TaskCreate':
    case 'TaskUpdate':
      return desc || (typeof input.status === 'string' ? input.status : '')
    case 'LSP':
      return typeof input.command === 'string' ? input.command : desc
    case 'SendMessage':
      return typeof input.recipient === 'string' ? `to ${input.recipient}` : desc
    case 'Skill':
      return typeof input.skill === 'string' ? `/${input.skill}` : desc
    default:
      return desc
  }
}

// ─── Login Card Component ────────────────────────────────────────────────────

function LoginCard({
  loginState,
  onStartLogin,
  onSubmitCode,
}: {
  loginState: LoginState
  onStartLogin: () => void
  onSubmitCode?: (code: string) => void
}) {
  // Login complete — briefly show success, then hide
  if (loginState.status === 'complete') {
    return (
      <div className="rounded-lg border border-green-500/30 bg-green-500/10 p-3">
        <div className="flex items-center gap-2 text-sm font-medium text-green-600 dark:text-green-400">
          <Check className="w-4 h-4" />
          Logged in successfully
        </div>
      </div>
    )
  }

  // Awaiting URL from bridge
  if (loginState.status === 'awaiting_url') {
    return (
      <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 space-y-2">
        <div className="flex items-center gap-2 text-sm font-medium text-primary">
          <Loader2 className="w-4 h-4 animate-spin" />
          Starting login...
        </div>
      </div>
    )
  }

  // Have URL — show link + code paste input
  if ((loginState.status === 'awaiting_code' || loginState.status === 'submitting') && loginState.url) {
    return <LoginCodeCard url={loginState.url} submitting={loginState.status === 'submitting'} onSubmitCode={onSubmitCode!} />
  }

  // Submitting code
  if (loginState.status === 'submitting') {
    return (
      <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 space-y-2">
        <div className="flex items-center gap-2 text-sm font-medium text-primary">
          <Loader2 className="w-4 h-4 animate-spin" />
          Authenticating...
        </div>
      </div>
    )
  }

  // Initial state or error — show login button
  return (
    <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 space-y-3">
      <div className="flex items-center gap-2 text-sm font-medium text-primary">
        <LogIn className="w-4 h-4" />
        Authentication required
      </div>
      <p className="text-sm text-muted-foreground">
        Sign in with your Anthropic account to use Claude Code.
      </p>
      {loginState.status === 'error' && loginState.error && loginState.error !== 'not_authenticated' && (
        <p className="text-sm text-destructive">{loginState.error}</p>
      )}
      <button
        onClick={onStartLogin}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-sm hover:bg-primary/90 transition-colors"
      >
        <LogIn className="w-3.5 h-3.5" />
        Sign in
      </button>
    </div>
  )
}

function LoginCodeCard({ url, submitting, onSubmitCode }: { url: string; submitting: boolean; onSubmitCode: (code: string) => void }) {
  const [code, setCode] = useState('')

  return (
    <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 space-y-3">
      <div className="flex items-center gap-2 text-sm font-medium text-primary">
        <Key className="w-4 h-4" />
        Sign in with your Claude subscription
      </div>

      <div className="space-y-2 text-sm">
        <p className="text-muted-foreground">
          1. Click below to open the Anthropic login page
        </p>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-sm hover:bg-primary/90 transition-colors"
        >
          <ExternalLink className="w-3.5 h-3.5" />
          Open Anthropic Login
        </a>
        <p className="text-muted-foreground">
          2. After logging in, paste the code shown on the page:
        </p>
      </div>

      <div className="flex gap-2">
        <input
          type="text"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && code.trim() && !submitting) onSubmitCode(code.trim())
          }}
          placeholder="Paste authorization code..."
          disabled={submitting}
          className="flex-1 rounded-lg border border-border bg-card px-3 py-1.5 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50"
          autoFocus
        />
        <button
          onClick={() => { if (code.trim()) onSubmitCode(code.trim()) }}
          disabled={!code.trim() || submitting}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-sm hover:bg-primary/90 disabled:opacity-50 transition-colors"
        >
          {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
          {submitting ? 'Verifying...' : 'Submit'}
        </button>
      </div>
    </div>
  )
}

// ─── Subagent Thread ────────────────────────────────────────────────────────

// Memoized for the same reason as MessageBubble — parent re-renders every
// keystroke, and these threads contain MessageBubbles whose markdown would
// otherwise re-parse on each one. Inner function is renamed so the recursive
// <SubagentThread/> reference below resolves to the outer (memoized) const,
// not the unwrapped named function expression.
const SubagentThread = memo(function SubagentThreadImpl({
  description,
  agentType,
  messages: kids,
  childrenByParent,
  agentMeta,
  isStreaming,
}: {
  description: string
  agentType: string
  messages: ChatMessage[]
  childrenByParent: Map<string, ChatMessage[]>
  agentMeta: Map<string, { description: string; agentType: string }>
  isStreaming: boolean
}) {
  // Derive status from children: has result → completed, has assistant → running
  const lastKid = kids[kids.length - 1]
  const status = lastKid?.type === 'result'
    ? (lastKid.data?.is_error ? 'failed' : 'completed')
    : (kids.some(m => m.type === 'assistant') ? 'running' : 'pending')

  // Always default collapsed. Long-running subagents produce dozens of
  // lines of intermediate output that drown out the main conversation,
  // and on reload the previous in-memory "open" state is gone — so the
  // user kept seeing every old subagent re-expanded. Tap to expand;
  // status pip on the header tells you which ones finished and how.
  const [open, setOpen] = useState(false)

  const statusColors: Record<string, string> = {
    running: 'text-blue-500',
    completed: 'text-green-500',
    failed: 'text-red-500',
    pending: 'text-muted-foreground/40',
  }

  // Preview: last text content when collapsed
  const preview = !open ? (() => {
    for (let i = kids.length - 1; i >= 0; i--) {
      const m = kids[i]
      if (m.type === 'assistant') {
        const text = m.message?.content?.find((b: ContentBlock) => b.type === 'text')?.text
        if (text) return text.substring(0, 60) + (text.length > 60 ? '...' : '')
      }
    }
    return ''
  })() : ''

  return (
    <div className="ml-4 border-l-2 border-primary/20 pl-3 my-2">
      <button
        type="button"
        onClick={() => setOpen(p => !p)}
        className="flex items-center gap-1.5 py-1 text-left cursor-pointer group w-full"
      >
        <ChevronRight className={`w-3 h-3 text-muted-foreground transition-transform shrink-0 ${open ? 'rotate-90' : ''}`} />
        <span className="text-xs font-medium text-primary/70">{description}</span>
        {agentType && <span className="text-[10px] text-muted-foreground/50">{agentType}</span>}
        <span className={`text-[10px] ${statusColors[status]}`}>
          {status === 'running' ? '●' : status === 'completed' ? '✓' : status === 'failed' ? '✗' : '○'}
        </span>
        {!open && preview && (
          <span className="text-[10px] text-muted-foreground/40 truncate ml-1">{preview}</span>
        )}
        <span className="text-[10px] text-muted-foreground/40 ml-auto shrink-0">{kids.length}</span>
      </button>
      {open && (
        <div className="space-y-2 pb-2 mt-1">
          {kids.map((msg, j) => (
            <div key={j}>
              {/* Subagent thread messages never carry Copy/Listen — those belong
                  only on the top-level final reply. */}
              <MessageBubble message={msg} isLast={j === kids.length - 1} isFinalReply={false} isStreaming={isStreaming} />
              {/* Recursive: nested subagent threads */}
              {msg.type === 'assistant' && (msg.message?.content || []).map(block => {
                if (block.type !== 'tool_use' || block.name !== 'Agent' || !block.id) return null
                const nested = childrenByParent.get(block.id)
                if (!nested || nested.length === 0) return null
                const info = agentMeta.get(block.id)
                return (
                  <SubagentThread
                    key={block.id}
                    description={info?.description || 'Subagent'}
                    agentType={info?.agentType || ''}
                    messages={nested}
                    childrenByParent={childrenByParent}
                    agentMeta={agentMeta}
                    isStreaming={isStreaming}
                  />
                )
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  )
})

// ─── Permission Card Component ──────────────────────────────────────────────

function PermissionCard({
  request,
  onRespond,
}: {
  request: PermissionRequest
  onRespond: (requestId: string, behavior: 'allow' | 'deny') => void
}) {
  return (
    <div className="rounded-lg border border-yellow-500/50 bg-yellow-500/10 p-3 space-y-2">
      <div className="flex items-center gap-2 text-sm font-medium text-yellow-600 dark:text-yellow-400">
        <Loader2 className="w-4 h-4 animate-spin" />
        Permission Request
      </div>
      <div className="text-sm">
        <span className="font-medium">{request.tool_name}</span>
        {request.description && (
          <p className="text-muted-foreground mt-1">{request.description}</p>
        )}
        {request.input && Object.keys(request.input).length > 0 && (
          <pre className="mt-2 text-xs bg-secondary/50 rounded p-2 overflow-x-auto whitespace-pre-wrap">
            {JSON.stringify(request.input, null, 2).substring(0, 500)}
          </pre>
        )}
      </div>
      <div className="flex gap-2">
        <button
          onClick={() => onRespond(request.request_id, 'allow')}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded bg-green-600 text-white text-sm hover:bg-green-700 transition-colors"
        >
          <Check className="w-3.5 h-3.5" />
          Allow
        </button>
        <button
          onClick={() => onRespond(request.request_id, 'deny')}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded bg-red-600 text-white text-sm hover:bg-red-700 transition-colors"
        >
          <X className="w-3.5 h-3.5" />
          Deny
        </button>
      </div>
    </div>
  )
}

// ─── Exit Plan Mode Card ────────────────────────────────────────────────────

function ExitPlanModeCard({
  requestId,
  description,
  onRespond,
}: {
  requestId: string
  description?: string
  onRespond: (requestId: string, answers: Record<string, string>, behavior?: 'allow' | 'deny') => void
}) {
  const [showRevise, setShowRevise] = useState(false)
  const [reviseText, setReviseText] = useState('')

  const handleAccept = () => {
    onRespond(requestId, { '0': 'Yes, proceed with the plan' })
  }

  const handleRevise = () => {
    if (!reviseText.trim()) return
    onRespond(requestId, { '0': reviseText.trim() }, 'deny')
  }

  return (
    <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 space-y-3">
      <div className="flex items-center gap-2 text-sm font-medium text-primary">
        <MessageCircleQuestion className="w-4 h-4" />
        Plan ready
      </div>
      {description && <p className="text-sm text-muted-foreground">{description}</p>}
      <p className="text-sm">Would you like to proceed?</p>

      <div className="space-y-1.5">
        {/* Accept option */}
        <button
          onClick={handleAccept}
          className="w-full text-left px-3 py-2 rounded-lg border border-border bg-card/50 hover:bg-card hover:border-primary/30 transition-all"
        >
          <div className="flex items-center gap-2">
            <span className="w-5 h-5 rounded-full border-2 border-muted-foreground/30 flex items-center justify-center shrink-0 text-xs font-medium text-muted-foreground">
              1
            </span>
            <span className="text-sm font-medium">Yes, proceed with the plan</span>
          </div>
        </button>

        {/* Revise option */}
        <button
          onClick={() => setShowRevise(!showRevise)}
          className={`w-full text-left px-3 py-2 rounded-lg border transition-all ${
            showRevise
              ? 'border-primary/30 bg-primary/5'
              : 'border-border bg-card/50 hover:bg-card hover:border-primary/30'
          }`}
        >
          <div className="flex items-center gap-2">
            <span className="w-5 h-5 rounded-full border-2 border-muted-foreground/30 flex items-center justify-center shrink-0 text-xs font-medium text-muted-foreground">
              2
            </span>
            <span className="text-sm font-medium">Tell Claude what to change</span>
          </div>
        </button>

        {showRevise && (
          <div className="flex gap-2 pl-7">
            <input
              type="text"
              value={reviseText}
              onChange={(e) => setReviseText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && reviseText.trim()) handleRevise()
              }}
              placeholder="Describe what to change..."
              className="flex-1 rounded-lg border border-border bg-card px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
              autoFocus
            />
            <button
              onClick={handleRevise}
              disabled={!reviseText.trim()}
              className="px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-sm hover:bg-primary/90 disabled:opacity-50 transition-colors"
            >
              Send
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Question Card Component (AskUserQuestion) ─────────────────────────────

function QuestionCard({
  request,
  onRespond,
}: {
  request: QuestionRequest
  onRespond: (requestId: string, answers: Record<string, string>, behavior?: 'allow' | 'deny') => void
}) {
  const [customText, setCustomText] = useState('')
  const [answers, setAnswers] = useState<Record<string, string>>({})
  const questions: QuestionItem[] = Array.isArray(request.input.questions) ? request.input.questions : []
  const toolName = request.tool_name || ''

  // ExitPlanMode — show plan acceptance with options matching vanilla CLI
  if (toolName === 'ExitPlanMode') {
    return (
      <ExitPlanModeCard
        requestId={request.request_id}
        description={request.description}
        onRespond={onRespond}
      />
    )
  }

  // Single question string fallback
  if (questions.length === 0 && typeof request.input.question === 'string') {
    return (
      <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 space-y-2">
        <div className="flex items-center gap-2 text-sm font-medium text-primary">
          <MessageCircleQuestion className="w-4 h-4" />
          Claude is asking
        </div>
        <p className="text-sm">{request.input.question}</p>
        <div className="flex gap-2">
          <input
            type="text"
            value={customText}
            onChange={(e) => setCustomText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && customText.trim()) {
                onRespond(request.request_id, { '0': customText.trim() })
              }
            }}
            placeholder="Type your answer..."
            className="flex-1 rounded-lg border border-border bg-card px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
            autoFocus
          />
          <button
            onClick={() => {
              if (customText.trim()) onRespond(request.request_id, { '0': customText.trim() })
            }}
            disabled={!customText.trim()}
            className="px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-sm hover:bg-primary/90 disabled:opacity-50 transition-colors"
          >
            Send
          </button>
        </div>
      </div>
    )
  }

  // No questions at all — fallback with text input
  if (questions.length === 0) {
    const desc = request.description || ''
    return (
      <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 space-y-2">
        <div className="flex items-center gap-2 text-sm font-medium text-primary">
          <MessageCircleQuestion className="w-4 h-4" />
          Claude is asking
        </div>
        {desc && <p className="text-sm">{desc}</p>}
        <div className="flex gap-2">
          <input
            type="text"
            value={customText}
            onChange={(e) => setCustomText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && customText.trim()) {
                onRespond(request.request_id, { '0': customText.trim() })
              }
            }}
            placeholder="Type your answer..."
            className="flex-1 rounded-lg border border-border bg-card px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
            autoFocus
          />
          <button
            onClick={() => {
              if (customText.trim()) onRespond(request.request_id, { '0': customText.trim() })
            }}
            disabled={!customText.trim()}
            className="px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-sm hover:bg-primary/90 disabled:opacity-50 transition-colors"
          >
            Send
          </button>
        </div>
      </div>
    )
  }

  // Multi-question: collect all answers before sending
  const handleSelect = (index: number, label: string) => {
    const newAnswers = { ...answers, [String(index)]: label }
    setAnswers(newAnswers)
    // Auto-submit when all questions are answered
    if (Object.keys(newAnswers).length === questions.length) {
      onRespond(request.request_id, newAnswers)
    }
  }

  const answeredCount = Object.keys(answers).length

  return (
    <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 space-y-3">
      <div className="flex items-center gap-2 text-sm font-medium text-primary">
        <MessageCircleQuestion className="w-4 h-4" />
        Claude is asking
        {questions.length > 1 && (
          <span className="text-xs text-muted-foreground font-normal">
            ({answeredCount}/{questions.length})
          </span>
        )}
      </div>
      {questions.map((q, i) => {
        const answered = answers[String(i)]
        return (
          <QuestionDisplay
            key={i}
            question={q}
            answered={answered}
            onSelect={(label) => handleSelect(i, label)}
          />
        )
      })}
    </div>
  )
}

function QuestionDisplay({
  question,
  answered,
  onSelect,
}: {
  question: QuestionItem
  answered?: string
  onSelect: (label: string) => void
}) {
  const [customText, setCustomText] = useState('')
  const [showCustom, setShowCustom] = useState(false)

  // Already answered — show selected answer
  if (answered) {
    return (
      <div className="space-y-2 opacity-60">
        {question.header && (
          <span className="inline-block text-[10px] font-semibold text-primary bg-primary/10 px-1.5 py-0.5 rounded">
            {question.header}
          </span>
        )}
        {question.question && (
          <p className="text-sm leading-relaxed">{question.question}</p>
        )}
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-primary/30 bg-primary/10">
          <Check className="w-4 h-4 text-primary shrink-0" />
          <span className="text-sm font-medium">{answered}</span>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-2">
      {question.header && (
        <span className="inline-block text-[10px] font-semibold text-primary bg-primary/10 px-1.5 py-0.5 rounded">
          {question.header}
        </span>
      )}
      {question.question && (
        <p className="text-sm leading-relaxed">{question.question}</p>
      )}
      {question.options.length > 0 && (
        <div className="space-y-1.5">
          {question.options.map((opt, j) => {
            const label = typeof opt === 'string' ? opt : (opt.label || String(opt))
            const desc = typeof opt === 'object' ? opt.description : undefined
            return (
              <button
                key={j}
                onClick={() => onSelect(label)}
                className="w-full text-left px-3 py-2 rounded-lg border border-border bg-card/50 hover:bg-card hover:border-primary/30 transition-all"
              >
                <div className="flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full border-2 border-muted-foreground/30 flex items-center justify-center shrink-0 text-xs font-medium text-muted-foreground">
                    {j + 1}
                  </span>
                  <div>
                    <span className="text-sm font-medium">{label}</span>
                    {desc && <p className="text-xs text-muted-foreground mt-0.5">{desc}</p>}
                  </div>
                </div>
              </button>
            )
          })}

          {/* Custom answer option */}
          <button
            onClick={() => setShowCustom(!showCustom)}
            className={`w-full text-left px-3 py-2 rounded-lg border transition-all ${
              showCustom
                ? 'border-primary/30 bg-primary/5'
                : 'border-border bg-card/50 hover:bg-card hover:border-primary/30'
            }`}
          >
            <div className="flex items-center gap-2">
              <span className="w-5 h-5 rounded-full border-2 border-muted-foreground/30 flex items-center justify-center shrink-0 text-xs text-muted-foreground">
                ?
              </span>
              <span className="text-sm text-muted-foreground">Type something else...</span>
            </div>
          </button>

          {showCustom && (
            <div className="flex gap-2 pl-7">
              <input
                type="text"
                value={customText}
                onChange={(e) => setCustomText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && customText.trim()) {
                    onSelect(customText.trim())
                  }
                }}
                placeholder="Type your answer..."
                className="flex-1 rounded-lg border border-border bg-card px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
                autoFocus
              />
              <button
                onClick={() => {
                  if (customText.trim()) onSelect(customText.trim())
                }}
                disabled={!customText.trim()}
                className="px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-sm hover:bg-primary/90 disabled:opacity-50 transition-colors"
              >
                Send
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
